// Typed client for the console API (platform/docs/api-phase0.md, api-phase1.md, api-phase2.md and api-phase3.md).
// Every /api call goes through request(), which adds the auth headers from the active AuthProvider.

import type { AuthProvider } from './auth.ts';

/* ------------------------------------------------------------------ */
/* Shared                                                               */
/* ------------------------------------------------------------------ */

export type Role = 'operator' | 'supervisor' | 'admin' | 'auditor';
export const ALL_ROLES: readonly Role[] = ['operator', 'supervisor', 'admin', 'auditor'];

export type Source = 'import' | 'erp';

/** A JSON value as returned by the API for opaque payload objects. */
export type Json = string | number | boolean | null | Json[] | { [key: string]: Json };
export type JsonObject = { [key: string]: Json };

export interface Me {
  userId: string;
  upn: string;
  displayName: string;
  roles: Role[];
}

export interface Health {
  ok: boolean;
  version: string;
}

/* ------------------------------------------------------------------ */
/* Master data                                                          */
/* ------------------------------------------------------------------ */

export interface Company {
  id: string;
  name: string;
  legalName: string | null;
  status: string;
  tier: string | null;
  accountNumber: string | null;
  siteCount: number;
  source: Source;
  version: number;
  appliedAt: string | null;
  erpUrl: string | null;
  active: boolean;
}

export interface Address {
  line1: string | null;
  line2: string | null;
  city: string | null;
  region: string | null;
  postal_code: string | null;
  country: string | null;
}

export interface ServicePlan {
  code: string;
  name: string;
  features: string[];
}

export interface WeeklyWindow {
  days: string[];
  start: string;
  end: string;
}

export interface HoursException {
  date: string;
  mode: string;
  windows: { start: string; end: string }[];
  label: string | null;
}

/** contracted_hours from the site payload (contract §site). */
export interface ContractedHours {
  mode: string;
  weekly?: WeeklyWindow[];
  exceptions?: HoursException[];
  effective_from?: string | null;
  effective_to?: string | null;
}

export interface SiteSummary {
  id: string;
  companyId: string;
  companyName: string;
  name: string;
  siteCode: string | null;
  status: string;
  timeZone: string;
  address: Address | null;
  crossStreets: string | null;
  geo: { lat: number; lon: number } | null;
  vertical: string | null;
  servicePlan: ServicePlan | null;
  contractedHours: ContractedHours | null;
  inContractedHoursNow: boolean | null;
  /** Contract 1.5: set in the ERP (null: not paused), and whether it is in force now (its until not passed). */
  alarmsPaused?: { since: string; note: string | null; until: string | null } | null;
  alarmsPausedNow?: boolean;
  cameraCount: number;
  deviceCount: number;
  source: Source;
  version: number;
  appliedAt: string | null;
  erpUrl: string | null;
  active: boolean;
  held: boolean;
}

export interface AnalyticsBinding {
  engine: string;
  analytics_device_id: string;
  channel: number | string | null;
}

export interface CameraCapabilities {
  ptz: boolean;
  two_way_audio: boolean;
  outputs: number | boolean | null;
  ir: boolean;
  thermal: boolean;
}

export type HealthStatus = 'online' | 'offline' | 'unauthorized' | 'unknown';

export interface CameraHealth {
  status: HealthStatus;
  lastSeenAt: string | null;
  changedAt: string | null;
}

export interface Camera {
  id: string;
  siteId: string;
  name: string;
  displayNumber: number | null;
  status: string;
  viewType: string | null;
  locationDescription: string | null;
  nxSystemId: string | null;
  nxCameraId: string | null;
  /** Deprecated in contract 1.4 (alarm endpoints replace bindings). */
  analyticsBindings: AnalyticsBinding[];
  /** Contract 1.4: the ERP asset tag, part of the camera's alarm endpoints. */
  assetTag?: string | null;
  /** Contract 1.4 endpoints (4.7); only for supervisors and admins (they carry the site's alarm key). */
  alarmEndpoints?: AlarmEndpoints | null;
  /** Last alarm received through one of this camera's endpoints. */
  lastEndpointAlarmAt?: string | null;
  capabilities: CameraCapabilities | null;
  health: CameraHealth | null;
  source: Source;
  version: number;
  active: boolean;
  held: boolean;
  erpUrl: string | null;
}

export type DeviceKind = 'speaker' | 'relay' | 'io_module';

export interface Device {
  id: string;
  siteId: string;
  kind: DeviceKind;
  name: string;
  status: string;
  locationDescription: string | null;
  hostCameraId: string | null;
  ioModuleId: string | null;
  channel: number | null;
  /** The speaker / relay / io_module object from the payload. */
  detail: JsonObject | null;
  source: Source;
  version: number;
  active: boolean;
  erpUrl: string | null;
}

export interface ContactPhone {
  phone_id: string;
  type: string;
  e164: string;
  extension: string | null;
  is_primary: boolean;
  /** Phase 5: the number takes texts (customer notification recipients need one). */
  sms_capable?: boolean;
}

export interface ContactEmail {
  email_id: string;
  address: string;
  is_primary: boolean;
}

export interface Contact {
  id: string;
  companyId: string;
  displayName: string;
  jobTitle: string | null;
  contactType: string;
  status: string;
  phones: ContactPhone[];
  emails: ContactEmail[];
  siteLinks: { site_id: string; roles: string[] }[];
  /** Contract 1.3: roles across the whole company (e.g. primary), for a company-wide contact. */
  companyRoles: string[];
  /** Seen from a site: serves this site, or the whole company (no site links). Set on site and setup reads. */
  scope?: 'site' | 'company';
  source: Source;
  version: number;
  active: boolean;
  erpUrl: string | null;
}

export interface Fov {
  bearing_deg: number;
  angle_deg: number;
  range_px: number;
}

export interface Placement {
  entity_type: string;
  entity_id: string;
  x: number;
  y: number;
  label: string | null;
  fov: Fov | null;
}

export interface Zone {
  zone_id: string;
  name: string;
  polygon: [number, number][];
}

export interface Sitemap {
  id: string;
  siteId: string;
  name: string;
  revisionLabel: string | null;
  sortOrder: number;
  imageUrl: string;
  image: { width_px: number; height_px: number; content_type: string };
  placements: Placement[];
  zones: Zone[];
  source: Source;
  version: number;
}

export interface SiteIngestAddress {
  id: string;
  address: string;
  active: boolean;
  lastReceivedAt: string | null;
  testReceivedAt: string | null;
}

export interface AlarmEndpoints {
  email: string;
  url: string;
}

export interface SiteDetail extends SiteSummary {
  /** Site-level endpoints (one device, many cameras); supervisors and admins only. */
  alarmEndpoints?: AlarmEndpoints | null;
  lastSiteEndpointAlarmAt?: string | null;
  cameras: Camera[];
  devices: Device[];
  contacts: Contact[];
  sitemaps: Sitemap[];
  ingestAddresses: SiteIngestAddress[];
}

/* ------------------------------------------------------------------ */
/* Alarms and video                                                     */
/* ------------------------------------------------------------------ */

export type AlarmFlag =
  | 'unmapped_camera'
  | 'unknown_site'
  | 'clock_skew'
  | 'rate_limited'
  | 'unparsed'
  | 'outside_schedule';

export interface AlarmSummary {
  id: string;
  displayNo: string | number | null;
  siteId: string | null;
  siteName: string | null;
  siteTimeZone: string | null;
  cameraId: string | null;
  cameraName: string | null;
  analytic: string | null;
  occurredAt: string | null;
  receivedAt: string;
  normalizedAt: string | null;
  latencyMs: number | null;
  burstCount: number;
  /** Known values are AlarmFlag; unknown future flags are shown as neutral pills. */
  flags: string[];
  vendor: string | null;
  vendorConfidence: number | null;
  snapshotUrl: string | null;
  hasClip: boolean;
  inboundMessageId: string | null;
}

export interface AlarmMedia {
  id: string;
  kind: 'snapshot' | 'clip' | 'archive_export';
  url: string;
  contentType: string;
  fileName: string | null;
  size: number | null;
}

export interface AlarmMessage {
  id: string;
  rcptTo: string;
  smtpFrom: string;
  receivedAt: string;
  parseStatus: string;
  parserTemplate: { id: string; vendor: string; version: number | string } | null;
  parseError: string | null;
  rawUrl: string | null;
}

export interface AlarmDetail extends AlarmSummary {
  media: AlarmMedia[];
  vendorPayload: JsonObject | null;
  message: AlarmMessage | null;
}

export type StreamKind = 'sim' | 'media' | 'mjpeg' | 'webrtc';
export type StreamMode = 'live' | 'archive';

let hevc: boolean | null = null;
/**
 * Whether this browser decodes H.265 in a <video>: Chrome and Edge do with the GPU's decoder (Windows needs the HEVC
 * Video Extensions), Safari does, Firefox doesn't. When it can't, NX transcodes H.265 cameras to VP8 at full size.
 */
export function playsHevc(): boolean {
  if (hevc === null) {
    try {
      hevc = document.createElement('video').canPlayType('video/mp4; codecs="hvc1.1.6.L120.90"') === 'probably';
    } catch {
      hevc = false;
    }
  }
  return hevc;
}

export interface StreamDescriptor {
  kind: StreamKind;
  mode: StreamMode;
  at: string | null;
  url: string | null;
  expiresAt: string | null;
  camera: { id: string; name: string; siteId: string; siteTimeZone: string };
  sim?: { seed: string; label: string };
  /** Watch-time view for this session (api-phase1-telephony-audit.md); absent on older servers. */
  viewId?: string | null;
}

export type ViewEndReason = 'switched' | 'closed' | 'paused' | 'hidden' | 'unload';

/* ------------------------------------------------------------------ */
/* Imports (erp-sync)                                                   */
/* ------------------------------------------------------------------ */

export type ImportStatus = 'rejected' | 'previewed' | 'applying' | 'applied' | 'cancelled' | 'failed';
export type ChangeType = 'create' | 'update' | 'unchanged' | 'delete' | 'reactivate';

export interface ImportError {
  entityIndex: number | null;
  entityType: string | null;
  entityId: string | null;
  path: string | null;
  message: string;
}

export interface ImportSummary {
  creates: number;
  updates: number;
  unchanged: number;
  deletes: number;
  reactivates: number;
  holds: number;
  warnings: number;
}

export interface PlanItem {
  entityType: string;
  entityId: string;
  siteId: string | null;
  change: ChangeType;
  fromVersion: number | null;
  toVersion: number | null;
  hold: { rule: string; detail: string } | null;
  warnings: string[];
}

export interface ImportRecord {
  id: string;
  fileId: string;
  fileName: string;
  uploadedBy: string;
  uploadedAt: string;
  sha256: string;
  size: number;
  source: string;
  scope: Json;
  status: ImportStatus;
  errors: ImportError[];
  summary: ImportSummary | null;
  plan?: PlanItem[];
  confirmedBy: string | null;
  appliedAt: string | null;
}

export interface Hold {
  id: string;
  entityType: string;
  entityId: string;
  siteId: string | null;
  siteName: string | null;
  rule: string;
  detail: string;
  change: 'delete' | 'deactivate' | 'status_change';
  status: string;
  createdAt: string;
  decidedBy: string | null;
  decidedAt: string | null;
  queuedCount: number;
}

export interface DeadLetter {
  id: string;
  entityType: string;
  entityId: string;
  reason: string;
  detail: string | null;
  status: string;
  createdAt: string;
  message: Json;
}

export interface WorkItem {
  id: string;
  kind: 'drift' | 'data_issue' | 'held_change' | 'notice';
  siteId: string | null;
  title: string;
  detail: string | null;
  status: string;
  createdAt: string;
}

export interface ApplyLogEntry {
  id: string;
  at: string;
  source: string;
  importFileId: string | null;
  entityType: string;
  entityId: string;
  siteId: string | null;
  version: number | null;
  result: string;
  effect: string | null;
  reason: string | null;
}

/* ------------------------------------------------------------------ */
/* Phase 6: ERP Sync monitor and shadow cutover (contract §6.7–§6.11)   */
/* ------------------------------------------------------------------ */

export type FeedMode = 'off' | 'shadow' | 'live';

export interface FeedState {
  mode: FeedMode;
  promotedAt?: string | null;
  rollbackUntil?: string | null;
  shadowSince?: string | null;
}

export interface ErpAlert {
  level: 'critical' | 'warning' | 'info';
  code: string;
  text: string;
}

export type ResyncScope = { kind: 'all' } | { kind: 'site'; site_id: string } | { kind: 'entities'; entities: { entity_type: string; entity_id: string }[] };

export interface DigestSummary {
  entities: number;
  inSync: number;
  diffs: number;
  missing: number;
  newerInErp: number;
  hashMismatch: number;
  versionWentBackwards: number;
  missedDeletes: number;
  vanished: number;
  held: number;
  resyncRequested: number;
  parkedDeadLettered?: number;
  partial: boolean;
  dataset: string;
}

/** GET /api/sync/erp/status. */
export interface ErpStatus {
  feed: FeedState;
  receiver: {
    configured: boolean;
    validateEndpoint: boolean;
    pending: number;
    failed: number;
    lastReceivedAt: string | null;
    received24h: number;
    byStatus24h: Record<string, number>;
    lastAuthFailureAt: string | null;
    lastAuthFailure: string | null;
  };
  delivery: {
    configured: boolean;
    host: string | null;
    on: boolean;
    paused: { paused: boolean; reason?: string; status?: number; at?: string };
    pending: number;
    oldestPendingS: number;
    dead: number;
    heldUntilCutover: number;
    sent24h: number;
    lastSentAt: string | null;
  };
  parked: number;
  deadLetters: number;
  holds: number;
  openResyncRequests: number;
  lastDigest: {
    digestId: string;
    requestId: string | null;
    scope: ResyncScope;
    asOf: string | null;
    comparedAt: string | null;
    partial: boolean;
    summary: DigestSummary | null;
  } | null;
  alerts: ErpAlert[];
}

export type InboxStatus = 'received' | 'applied' | 'skipped_stale' | 'skipped_same' | 'parked' | 'held' | 'dead' | 'failed';
export const INBOX_STATUSES: readonly InboxStatus[] = ['received', 'applied', 'skipped_stale', 'skipped_same', 'parked', 'held', 'dead', 'failed'];

export interface InboxMessage {
  eventId: string;
  eventType: string;
  entityType: string | null;
  entityId: string | null;
  version: number;
  receivedAt: string | null;
  status: string;
  reason: string | null;
  inReplyTo: string | null;
  dataset: string | null;
  attempt: number | null;
  appliedAt: string | null;
}

export interface InboxMessageDetail {
  eventId: string;
  status: string;
  reason: string | null;
  result: Json;
  receivedAt: string | null;
  body: Json;
}

export type DeliveryStatus = 'pending' | 'sent' | 'dead' | 'held_until_phase6' | 'discarded';
export const DELIVERY_STATUSES: readonly DeliveryStatus[] = ['pending', 'sent', 'dead', 'held_until_phase6', 'discarded'];

export interface Delivery {
  eventId: string;
  eventType: string;
  entityType: string | null;
  entityId: string | null;
  version: number;
  status: string;
  attempts: number;
  nextAttemptAt: string | null;
  lastStatus: number | null;
  lastBody: string | null;
  lastError: string | null;
  createdAt: string | null;
  firstAttemptAt: string | null;
  lastAttemptAt: string | null;
  sentAt: string | null;
}

export interface ResyncRequest {
  requestId: string;
  reason: string;
  scope: ResyncScope;
  requestedBy: { type: string; user: { user_id: string; display_name: string } | null } | null;
  note: string | null;
  dataset: string | null;
  status: string;
  replies: number;
  digestId: string | null;
  createdAt: string;
  answeredAt: string | null;
}

export interface Digest {
  digestId: string;
  requestId: string | null;
  scope: ResyncScope;
  asOf: string;
  parts: number;
  partCount: number;
  status: string;
  firstPartAt: string;
  comparedAt: string | null;
  partial: boolean | null;
  summary: DigestSummary | null;
}

export interface DigestDiff {
  entityType: string;
  entityId: string;
  kind: 'missing' | 'newer_in_erp' | 'hash_mismatch' | 'version_went_backwards' | 'missed_delete' | 'vanished' | 'held' | 'dead_lettered';
  erpVersion: number | null;
  socVersion: number | null;
  action: 'resync' | 'delete' | 'alert' | 'none';
}

export interface DigestDetail extends Digest {
  diffs: DigestDiff[] | null;
}

export interface ParkedMessage {
  id: string;
  entityType: string;
  entityId: string;
  waitingFor: { entity_type: string; entity_id: string }[];
  parkedAt: string | null;
  nextResyncAt: string | null;
  resyncCount: number;
  lastRequestId: string | null;
  reason: string | null;
  dataset: string | null;
}

export interface CutoverHistoryEntry {
  id: string;
  action: string;
  by: string | null;
  at: string;
  summary: JsonObject | null;
}

/** GET /api/sync/cutover. */
export interface CutoverState extends FeedState {
  importsAllowed: boolean;
  deliveryOn: boolean;
  rollbackAvailable: boolean;
  activeCounts: Record<string, Record<string, number>>;
  ready: { receiver: boolean; delivery: boolean };
  history: CutoverHistoryEntry[];
}

export interface CutoverDiffItem {
  entityType: string;
  entityId: string;
  name: string | null;
  siteId: string | null;
  kind: 'different' | 'only_in_live' | 'only_in_shadow';
  fields: { path: string; live: Json; shadow: Json }[];
  accepted: { by: string; at: string; note: string | null } | null;
}

export interface CutoverDiff {
  generatedAt: string;
  ignoredFields: string[];
  counts: { live: number; shadow: number; same: number; different: number; onlyInLive: number; onlyInShadow: number; accepted: number; open: number };
  items: CutoverDiffItem[];
}

export interface PromoteResult {
  held: number;
  holdIds: string[];
  reducingApplied: number;
  supersededHolds: number;
  acceptedDifferences: number;
  backlogQueued: number;
  rollbackUntil: string | null;
}

export interface RollbackResult {
  supersededHolds: number;
  reappliedToShadow: number;
}

/* ------------------------------------------------------------------ */
/* Alarm ingest admin                                                   */
/* ------------------------------------------------------------------ */

export interface IngestAddress {
  id: string;
  localPart: string;
  address: string;
  siteId: string;
  siteName: string | null;
  parserTemplateId: string | null;
  parserVendor: string | null;
  active: boolean;
  lastReceivedAt: string | null;
  testReceivedAt: string | null;
  /** Set on an active address whose site has no contracted hours (its alarms auto-close). */
  warning: string | null;
}

export interface ParserTemplate {
  id: string;
  vendor: string;
  version: number;
  kind: string;
  status: string;
  priority: number;
  match: Json;
  spec: Json;
  createdAt: string;
}

export type ParseStatus = 'quarantined' | 'unparsed' | 'pending' | 'parsed';

export interface InboundMessage {
  id: string;
  receivedAt: string;
  rcptTo: string;
  smtpFrom: string;
  size: number;
  parseStatus: string;
  rateLimited: boolean;
  parseError: string | null;
  parserTemplateId: string | null;
  alarmIds: string[];
}

export interface TestParseResult {
  ok: boolean;
  matched: boolean;
  fields: JsonObject | null;
  alarm: {
    analytic: string | null;
    occurredAt: string | null;
    siteId: string | null;
    cameraId: string | null;
    flags: string[];
    attachments: Json[];
  } | null;
  error: string | null;
}

/* ------------------------------------------------------------------ */
/* Phase 1: events (platform/docs/api-phase1.md)                        */
/* ------------------------------------------------------------------ */

export type Severity = 'info' | 'low' | 'medium' | 'high' | 'critical';
export const SEVERITIES: readonly Severity[] = ['critical', 'high', 'medium', 'low', 'info'];
export type EventState =
  | 'agent_working'
  | 'settling'
  | 'queued'
  | 'offered'
  | 'active'
  | 'parked'
  | 'transferred'
  | 'dispositioned'
  | 'closed';
export type ActorType = 'agent' | 'operator' | 'supervisor' | 'admin' | 'system';
export interface Actor {
  type: ActorType;
  id: string | null;
  name: string | null;
}

export interface EventSummary {
  id: string;
  displayNo: string;
  siteId: string;
  siteName: string;
  siteTimeZone: string;
  companyName: string | null;
  state: EventState;
  severity: Severity;
  priority: number;
  version: number;
  alarmCount: number;
  cameraCount: number;
  firstAlarmAt: string;
  lastAlarmAt: string;
  queuedAt: string | null;
  slaDueAt: string | null;
  acceptedAt: string | null;
  owner: { id: string; name: string } | null;
  primaryAnalytic: string;
  primaryCameraName: string | null;
  snapshotUrl: string | null;
  storm: boolean;
  follows: { id: string; displayNo: string } | null;
  /** Site not live yet: only supervisors/admins see it in the queue. */
  commissioning: boolean;
  /* Phase 2 (api-phase2.md). Optional so a Phase 1 server still type-checks at runtime. */
  park?: ParkInfo | null;
  /** True until the owner opens the raised event. */
  raised?: boolean;
  /** Who a queued / transferred / raised event is waiting for. */
  target?: EventTarget | null;
  transferNote?: string | null;
  /** Moved to the overflow group because nobody in the target group was eligible in time. */
  overflowed?: boolean;
  /** The open offer for this event, if the router has offered it. */
  offer?: EventOfferInfo | null;
  /* Phase 3 (api-phase3.md). The AI verdict is only set at Level 1+ sites. */
  ai?: AiVerdictSummary | null;
  /** The agent is paused on this event (owner or supervisor). */
  agentPaused?: boolean;
}

/* ------------------------------------------------------------------ */
/* Phase 3: the AI agent (platform/docs/api-phase3.md)                  */
/* ------------------------------------------------------------------ */

/** The contract's `event.closed.ai.final_verdict` enum; also the human class in QA and the report. */
export type Verdict = 'clear' | 'authorized' | 'monitor' | 'suspicious' | 'criminal_life_safety';
export const VERDICTS: readonly Verdict[] = ['clear', 'authorized', 'monitor', 'suspicious', 'criminal_life_safety'];

export interface AiVerdictSummary {
  verdict: Verdict | string;
  confidence: number | null;
}

export type AgentRunStatus = 'running' | 'waiting' | 'paused' | 'handed_off' | 'completed' | 'failed' | 'absent';
export type AgentStepKind = 'observe' | 'assess' | 'act' | 'evaluate' | 'handoff' | 'message';
export type ToolDecision = 'allow' | 'ask' | 'deny' | 'shadow';

export interface AgentStep {
  seq: number;
  kind: AgentStepKind | string;
  /** The signal that started this turn: event_created, alarm_attached, pause, resume, escalated, event_closed. */
  trigger: string | null;
  rationale: string | null;
  output: { toolCalls?: string[]; stopReason?: string; text?: string } & Record<string, unknown> | null;
  shadow: boolean;
  model: string | null;
  tokensIn: number | null;
  tokensOut: number | null;
  cost: number | null;
  latencyMs: number | null;
  at: string;
}

export interface AgentToolCall {
  id: string;
  tool: string;
  args: Record<string, unknown> | null;
  decision: ToolDecision | string;
  /** hard_blocked, paused, budget, human_only, human_control, below_threshold, unreviewed_alarm, level, … */
  reason: string | null;
  status: 'succeeded' | 'failed' | 'denied' | 'shadow' | string;
  result: Record<string, unknown> | null;
  error: string | null;
  latencyMs: number | null;
  at: string;
}

export interface AgentUsage {
  model_calls?: number;
  tokens_in?: number;
  tokens_out?: number;
  cache_read?: number;
  cache_write?: number;
  cost?: number;
  wall_ms?: number;
  frames?: number;
}

export interface AgentBudgets {
  max_tool_calls: number;
  max_model_calls: number;
  max_wall_s: number;
  max_cost: number;
  max_frames: number;
}
export const BUDGET_KEYS: readonly (keyof AgentBudgets)[] = ['max_tool_calls', 'max_model_calls', 'max_wall_s', 'max_cost', 'max_frames'];

/** GET /api/events/:id/agent for operators at a Level 0 site. */
export interface AgentPanelHidden {
  visible: false;
  status: AgentRunStatus | string;
  liveLevel: number;
}

/** GET /api/events/:id/agent for supervisors, admins, and operators at Level 1+. */
export interface AgentPanelFull {
  visible: true;
  runId: string;
  status: AgentRunStatus | string;
  liveLevel: number;
  shadowLevel: number | null;
  /** The live level is 0: everything the agent did is a would-have. */
  shadow: boolean;
  paused: boolean;
  pausedScope: 'event' | 'site' | 'global' | null;
  verdict: Verdict | string | null;
  confidence: number | null;
  summary: string | null;
  handoffSummary: string | null;
  proposedDisposition: { code: string; notes?: string | null } | null;
  wouldResolve: boolean | null;
  usage: AgentUsage;
  budget: Partial<AgentBudgets>;
  model: { name: string; model: string; provider: string } | null;
  error: string | null;
  startedAt: string | null;
  endedAt: string | null;
  steps: AgentStep[];
  toolCalls: AgentToolCall[];
  /* Phase 3 follow-ups and Phase 4 (api-phase3.md "Changes after the console review", api-phase4.md). Optional so an
     older server still reads. */
  /** The latest record_assessment. painPoints are the site's pain points the agent matched, word for word. */
  assessment?: AgentAssessment | null;
  instructions?: Instruction[];
  approvals?: Approval[];
  drafts?: AgentDrafts;
  watches?: Watch[];
  /** Level 1+, not paused, run active. */
  acceptsInstructions?: boolean;
}

export interface AgentAssessment {
  verdict: Verdict | string | null;
  confidence: number | null;
  summary: string | null;
  subjects: { description?: string; location?: string }[];
  evidenceMediaIds: string[];
  painPoints: string[];
}

export type AgentPanel = AgentPanelHidden | AgentPanelFull;

/* ------------------------------------------------------------------ */
/* Phase 4: the agent as co-pilot (platform/docs/api-phase4.md)         */
/* ------------------------------------------------------------------ */

export type WatchCondition = 'police_arrival' | 'subject_return' | 'person_any' | 'vehicle_any' | 'gate_state' | 'custom';
export const WATCH_CONDITIONS: readonly WatchCondition[] = ['police_arrival', 'subject_return', 'person_any', 'vehicle_any', 'gate_state', 'custom'];

export interface WatchInput {
  cameraIds: string[];
  condition: WatchCondition;
  description?: string;
  /** 1..240; default: until the park fallback (or the vision default when not parked). */
  minutes?: number;
  /** 0..60 */
  minDwellS?: number;
  state?: 'open' | 'closed';
}

export type WatchStatus = 'active' | 'confirming' | 'hit' | 'expired' | 'cancelled';

export interface Watch {
  id: string;
  eventId: string;
  parkId: string | null;
  /** "operator:<userId>", "agent:<runId>", … */
  createdBy: string;
  byAgent: boolean;
  cameraIds: string[];
  cameraNames: string[];
  condition: WatchCondition | string;
  /** "police", "the subject coming back", … */
  conditionLabel: string;
  description: string | null;
  detector: 'local' | 'llm_confirm' | string;
  status: WatchStatus | string;
  createdAt: string;
  expiresAt: string;
  hitAt: string | null;
  hitCameraId: string | null;
  hitConfidence: number | null;
  /** /api/watches/:id/frame (auth; audited as media access). */
  frameUrl: string | null;
  rejectedCount: number;
  endedReason: string | null;
}

export type InstructionStatus = 'pending' | 'working' | 'answered' | 'failed' | 'cancelled';
export interface Instruction {
  id: string;
  eventId: string;
  userId: string;
  userName: string | null;
  text: string;
  at: string;
  status: InstructionStatus | string;
  reply: string | null;
  repliedAt: string | null;
}

export type ApprovalStatus = 'pending' | 'approved' | 'denied' | 'expired' | 'cancelled';
export interface Approval {
  id: string;
  eventId: string;
  tool: string;
  reason: string | null;
  summary: string;
  evidence: unknown;
  status: ApprovalStatus | string;
  requestedAt: string | null;
  expiresAt: string;
  /** A user id (the API has no name). */
  decidedBy: string | null;
  decidedAt: string | null;
  note: string | null;
}

/** SSE `approval` (to the event owner). */
export interface ApprovalMessage {
  approvalId: string;
  eventId: string;
  tool: string;
  summary: string;
  expiresAt: string;
}

export interface DispatchDraft {
  subjects: { description?: string; lastSeen?: string }[];
  vehicles: { description?: string; lastSeen?: string }[];
  direction: string;
  summary: string;
  createdAt: string | null;
}
export interface ReportDraft {
  narrative: string;
  createdAt: string | null;
}
export interface AgentDrafts {
  dispatch: DispatchDraft | null;
  report: ReportDraft | null;
}

/** `raise.ref` when the cause is watch_hit, and the `watch_hit` SSE message (hit on an event that isn't parked). */
export interface WatchHitRef {
  watchId: string;
  condition: string;
  cameraId: string | null;
  cameraName: string | null;
  frameUrl: string | null;
  confidence: number | null;
}

export interface GuardTourWindow {
  days?: string[];
  start: string;
  end: string;
}

export interface GuardTourRun {
  id: string;
  tourId: string;
  siteId: string;
  startedAt: string;
  finishedAt: string | null;
  status: 'running' | 'done' | string;
  camerasChecked: number | null;
  camerasUnavailable: number | null;
  exceptions: { cameraId: string; label: string; confidence: number; alarmId: string }[] | null;
  skipReason: string | null;
}

export interface GuardTour {
  id: string;
  siteId: string;
  name: string;
  active: boolean;
  everyMinutes: number;
  windows: GuardTourWindow[];
  cameraIds: string[];
  focus: ('person' | 'vehicle')[];
  minConfidence: number;
  lastRunAt: string | null;
  version: number;
  lastRun: GuardTourRun | null;
}

export interface GuardTourBody {
  name: string;
  cameraIds: string[];
  everyMinutes?: number;
  windows?: GuardTourWindow[];
  focus?: ('person' | 'vehicle')[];
  minConfidence?: number;
  active?: boolean;
}

export const SIM_LABELS = ['person', 'vehicle', 'police_vehicle', 'police_officer', 'emergency_lights', 'gate_open', 'gate_closed'] as const;
export type SimLabel = (typeof SIM_LABELS)[number];

/** GET /api/qa/:eventId/evidence: the blind evidence, no log, disposition or agent output. */
export interface QaEvidence {
  eventId: string;
  displayNo: string;
  firstAlarmAt: string;
  lastAlarmAt: string;
  site: EventSite;
  alarms: EventAlarm[];
  cameras: EventCamera[];
  sitemaps: Sitemap[];
  history: EventDetail['history'];
}

export type AuthorizedMatchPolicy = 'resolve' | 'escalate_low';

export interface AiPolicy {
  scope: 'global' | 'site' | 'inherited';
  siteId: string | null;
  /** Target level (what an admin set). */
  level: number;
  liveLevel: number;
  /** The level running in shadow until shadowUntil, then promoted. */
  shadowLevel: number | null;
  shadowUntil: string | null;
  autoResolveThreshold: number;
  /** Phase 5: below this confidence the agent asks before a talk-down or relay pulse (default 0.85). */
  deviceConfidenceThreshold?: number;
  authorizedMatchPolicy: AuthorizedMatchPolicy;
  qaSampleRate: number;
  budgets: AgentBudgets;
  deadManS: number;
  paused: boolean;
  pausedBy: string | null;
  /** Who paused it (api-phase3.md follow-ups). */
  pausedByName?: string | null;
  pauseReason: string | null;
  globalPaused: boolean;
  version: number;
  /** Two-stage triage (migration 021): stage 1 assesses behavior with a fast model; stage 2 is the full agent. */
  stage1?: Stage1Policy;
  /** A site's own stage 2 model (null: the global one, which is the current model). Always null globally. */
  stage2?: { modelConfigId: string | null };
  /** The level went live before its 14 days in shadow ended (an admin's override), until the next raise. */
  earlyLive?: { at: string; by: string | null; byName: string | null; reason: string; shadowDays: number } | null;
}

export type Stage1Mode = 'off' | 'shadow' | 'live';

export interface Stage1Policy {
  mode: Stage1Mode;
  /** Stage 1 keeps a routine clear / authorized event at or above this confidence. */
  minConfidence: number;
  /** This policy's own stage 1 model; on a site, null follows the global one. */
  modelConfigId: string | null;
  effectiveModelConfigId: string | null;
}

export interface AiPolicyPatch {
  level?: number;
  autoResolveThreshold?: number;
  deviceConfidenceThreshold?: number;
  authorizedMatchPolicy?: AuthorizedMatchPolicy;
  qaSampleRate?: number;
  deadManS?: number;
  budgets?: Partial<AgentBudgets>;
  stage1?: { mode?: Stage1Mode; minConfidence?: number; modelConfigId?: string | null };
  /** Sites only (globally, stage 2 is the current model: putAiModel). */
  stage2?: { modelConfigId: string | null };
}

export interface ModelConfig {
  id: string;
  name: string;
  provider: string;
  model: string;
  effort: string | null;
  promptVersion: string | null;
  current: boolean;
}

/** A model an admin adds to compare (POST /api/setup/ai/models). */
export interface NewModelConfig {
  name: string;
  provider: 'anthropic' | 'openrouter';
  model: string;
  effort: string;
}

export interface AutonomyLevel {
  level: number;
  name: string;
  description: string;
}

export interface AiSetup {
  /** cfg.setting agent.enabled: the runtime is deployed. */
  enabled: boolean;
  global: AiPolicy;
  /** Sites with their own policy row. */
  sites: { siteId: string; siteName: string; policy: AiPolicy }[];
  models: ModelConfig[];
  levels: AutonomyLevel[];
}

export type SiteAiPolicy = AiPolicy & { humanReviewOnly: boolean };

/** AI rules (api-phase3.md "AI rules"): what happens after the AI decides, first match wins. */
export type RulePriority = 'low' | 'normal' | 'high' | 'critical';
export interface RuleRecipients {
  to: 'customer' | 'staff';
  userIds?: string[];
  groupIds?: string[];
}
export interface AiRule {
  id: string;
  name: string;
  enabled: boolean;
  when: {
    verdicts?: string[];
    riskAtLeast?: 'low' | 'medium' | 'high';
    behaviors?: string[];
    confidenceBelow?: number;
    analytics?: string[];
    cameraIds?: string[];
    schedule?: { days: number[]; from: string; to: string };
    decidedBy?: 'stage1' | 'stage2';
  };
  then: {
    queue: { priority: RulePriority } | null;
    humanReview: boolean;
    notify: (RuleRecipients & { channel: 'email' | 'sms' | 'both'; template: string })[];
    report: (RuleRecipients & { expiresHours: number }) | null;
  };
}
export interface AiRuleSet {
  id: string;
  scope: 'global' | 'site' | 'inherited';
  siteId: string | null;
  version: number;
  /** What a save sends as expectedVersion: this scope's own list version (0 for a site that never saved its own). */
  editVersion: number;
  rules: AiRule[];
  createdAt: string;
  createdBy: string | null;
  note: string | null;
}
export interface AiRulesHistoryRow {
  id: string;
  version: number;
  note: string | null;
  createdBy: string | null;
  createdAt: string;
  inherit: boolean;
  rules: number;
}
export interface AiRulesSetup {
  ruleSet: AiRuleSet;
  history: AiRulesHistoryRow[];
  behaviors: string[];
  templates: { code: string; name: string; channel: string }[];
  staff: { id: string; name: string }[];
  groups: { id: string; name: string }[];
}
export interface AiRulesTest {
  days: number;
  events: number;
  byRule: { ruleId: string; name: string; matched: number }[];
  noMatch: number;
}

export interface QaReview {
  humanClass: Verdict | string;
  agree: boolean;
  notes: string | null;
  reviewer: string | null;
  reviewedAt: string | null;
}

export interface QaItem {
  eventId: string;
  /** Careful: carries `ai` at Level 1+ sites. The blind review must not show it. */
  event: EventSummary;
  sampledAt: string;
  reason: string | null;
  status: 'pending' | 'reviewed' | string;
  /** Null until the review is in. */
  ai: { verdict: Verdict | string; confidence: number | null; disposition: string | null } | null;
  review: QaReview | null;
}

export interface QaResult {
  eventId: string;
  agree: boolean;
  ai: { verdict: Verdict | string; confidence: number | null };
}

/** AI verdict × human class counts. */
export type VerdictMatrix = Record<string, Record<string, number>>;

export interface ReportStats {
  labeled: number;
  verdictAgreement: number | null;
  wouldResolve: number;
  wouldResolvePrecision: number | null;
  wouldResolveMissed: number;
  wouldResolveCoverage: number | null;
  matrix: VerdictMatrix;
}

export interface ReportSite extends ReportStats {
  siteId: string;
  siteName: string;
  policy: { liveLevel: number; shadowLevel: number | null; shadowUntil: string | null; paused: boolean };
  events: number;
  assessed: number;
  aiResolved: number;
  cost: number;
  qa: { sampled: number; reviewed: number; disagree: number };
}

export interface AgentReport {
  days: number;
  totals: ReportStats & { events: number; aiResolved: number };
  sites: ReportSite[];
}

/* ------------------------------------------------------------------ */
/* Phase 5: deter and resolve (platform/docs/api-phase5.md)             */
/* ------------------------------------------------------------------ */

export type RelayFunction = 'strobe' | 'siren' | 'lights' | 'gate' | 'horn' | 'lock' | 'other';
export type DeviceLiveState = 'idle' | 'playing' | 'on' | 'off' | 'unknown';

/** A speaker or relay at the event's site (GET /api/events/:id/devices). */
export interface EventDevice {
  id: string;
  name: string;
  kind: 'speaker' | 'relay';
  status: string;
  /** Speakers: the zone they cover; relays: where they are. */
  zone: string | null;
  function: RelayFunction | string | null;
  /** The longest a relay may stay on (a pulse never exceeds it). */
  hardwareMaxOnS: number | null;
  method: string | null;
  hostCameraId: string | null;
  state: DeviceLiveState | string;
  until: string | null;
}

export type TalkdownCategory = 'warning' | 'recording' | 'police' | 'leave' | 'custom';
export const TALKDOWN_CATEGORIES: readonly TalkdownCategory[] = ['warning', 'recording', 'police', 'leave', 'custom'];
export type TalkdownLanguage = 'en' | 'es';

/** A talk-down library message. Text may use {site} {camera} {zone} {time}. */
export interface TalkdownMessage {
  id: string;
  /** null = the global library. */
  siteId: string | null;
  name: string;
  category: TalkdownCategory | string;
  language: TalkdownLanguage | string;
  text: string;
  agentAllowed: boolean;
  active: boolean;
  sortOrder: number;
  version: number;
}

export interface TalkdownMessageBody {
  siteId?: string | null;
  name: string;
  category: TalkdownCategory;
  language: TalkdownLanguage;
  text: string;
  agentAllowed?: boolean;
  sortOrder?: number;
}

export interface EventDevices {
  speakers: EventDevice[];
  relays: EventDevice[];
  /** Active messages for this site: the site's own first, then the global library. */
  library: TalkdownMessage[];
  /** Relay functions the agent never drives (gate, lock). */
  humanOnlyFunctions: string[];
  /** The TTS engine ('simulated' until D10). */
  tts: string;
}

export interface TalkdownResult {
  actionId: string;
  devices: { deviceId: string; ok: boolean; error?: string; simulated?: boolean }[];
  durationMs: number;
  /** null for push-to-talk. */
  text: string | null;
  language: string;
  audioUrl: string;
}

export interface RelayResult {
  actionId: string;
  deviceId: string;
  function: string;
  action: 'pulse' | 'hold' | 'release';
  seconds: number | null;
  state: string;
  until: string | null;
  simulated: boolean;
}

/** SSE `device`: live state of a speaker or relay. */
export interface DeviceStateMessage {
  deviceId: string;
  siteId: string;
  kind: string;
  state: string;
  until: string | null;
}

export type NotifyChannel = 'sms' | 'email';
export type NotifyPurpose = 'event_notice' | 'customer_report';

export interface NotificationRecipient {
  id: string;
  siteId: string;
  contactId: string;
  contactName: string | null;
  channel: NotifyChannel;
  phoneId: string | null;
  emailId: string | null;
  purposes: NotifyPurpose[];
  active: boolean;
}

export interface NotificationRecipientBody {
  contactId: string;
  channel: NotifyChannel;
  phoneId?: string;
  emailId?: string;
  purposes?: NotifyPurpose[];
}

export interface NotificationTemplate {
  code: string;
  channel: NotifyChannel;
  language: string;
  purpose: NotifyPurpose;
  name: string;
  subject: string | null;
  body: string;
  agentAllowed: boolean;
  active: boolean;
}

export interface NotificationTemplateBody {
  purpose: NotifyPurpose;
  name: string;
  subject?: string | null;
  body: string;
  language?: string;
  agentAllowed?: boolean;
  active?: boolean;
}

export type NotificationStatus = 'queued' | 'sent' | 'simulated' | 'failed';

/** The moments an automatic site sends a notice at (services/core/src/auto-notify.ts). */
export type NotifyMoment = 'police_dispatched' | 'closed_genuine' | 'closed_clear';
export type NotifyMode = 'manual' | 'automatic';

/** GET/PUT /api/setup/sites/:id/notification-settings: manual or automatic notices, and each moment's template (null: off). */
export interface NotificationSettings {
  mode: NotifyMode;
  moments: Record<NotifyMoment, string | null>;
  defaults: Record<NotifyMoment, string | null>;
}

/** GET /api/events/:id/notifications: what was sent for the event. */
export interface EventNotification {
  id: string;
  purpose: NotifyPurpose | string;
  channel: NotifyChannel | string;
  contactId: string;
  contactName: string | null;
  templateCode: string;
  subject: string | null;
  body: string;
  status: NotificationStatus | string;
  provider: string | null;
  error: string | null;
  actorType: string;
  createdAt: string;
  sentAt: string | null;
}

/* ------------------------------------------------------------------ */
/* Phase 2: multi-event, routing, presence (platform/docs/api-phase2.md) */
/* ------------------------------------------------------------------ */

export type ParkReason = 'police_dispatched' | 'awaiting_callback' | 'monitoring' | 'waiting_on_customer';
export const PARK_REASONS: readonly { value: ParkReason; label: string }[] = [
  { value: 'police_dispatched', label: 'Police dispatched' },
  { value: 'awaiting_callback', label: 'Awaiting callback' },
  { value: 'monitoring', label: 'Monitoring' },
  { value: 'waiting_on_customer', label: 'Waiting on customer' },
];
export function parkReasonLabel(r: string | null | undefined): string {
  return PARK_REASONS.find((x) => x.value === r)?.label ?? (r ? r.replace(/_/g, ' ') : 'Parked');
}

export interface ParkInfo {
  reason: ParkReason;
  note: string | null;
  parkedAt: string;
  /** When the fallback timer raises the event. */
  fallbackAt: string;
}

export interface EventTarget {
  userId: string | null;
  userName: string | null;
  groupId: string | null;
  groupName: string | null;
}

export interface EventOfferInfo {
  userId: string;
  userName: string;
  expiresAt: string;
}

export type PresenceStatus = 'available' | 'busy' | 'break' | 'offline';
/** The statuses a user can set (offline comes from the stream). */
export type SettablePresence = Exclude<PresenceStatus, 'offline'>;
export const SETTABLE_PRESENCE: readonly SettablePresence[] = ['available', 'busy', 'break'];

export interface PresenceGroupRef {
  id: string;
  name: string;
  primary: boolean;
}

export interface Presence {
  userId: string;
  displayName: string;
  upn: string;
  roles: string[];
  status: PresenceStatus;
  /** null on servers that have never seen the user set a status. */
  since: string | null;
  routingPaused: boolean;
  /** An open console stream in the last 60 s. */
  online: boolean;
  activeCount: number;
  parkedCount: number;
  cap: number;
  groups: PresenceGroupRef[];
  skills: string[];
  offer: { id: string; eventId: string; expiresAt: string } | null;
}

/** An open offer: the `offer` SSE message and GET /api/offers/mine. */
export interface Offer {
  offerId: string;
  expiresAt: string;
  event: EventSummary;
  prestage: { cameraIds: string[] };
}

export type OfferWithdrawReason = 'expired' | 'taken' | 'withdrawn';
export interface OfferWithdrawn {
  offerId: string;
  reason: OfferWithdrawReason;
}

export type RaiseCause = 'timer' | 'new_alarm' | 'severity' | 'manual' | 'supervisor' | 'reassigned' | 'watch_hit';
export interface RaiseMessage {
  eventId: string;
  cause: RaiseCause;
  to: 'owner' | 'group';
  event: EventSummary;
  /** Phase 4: set when cause is watch_hit. */
  ref?: Partial<WatchHitRef>;
}

export interface ParkBody {
  reason: ParkReason;
  /** 5..120 */
  fallbackMinutes: number;
  note?: string;
  /** Phase 4: AI watches that raise the event when they hit (≤ 4). */
  watches?: WatchInput[];
}

/** Exactly one of toUserId / toGroupId. */
export type TransferBody = { toUserId: string; toGroupId?: undefined; note: string } | { toGroupId: string; toUserId?: undefined; note: string };

export type GroupKind = 'queue' | 'tier' | 'language' | 'supervisors';
export const GROUP_KINDS: readonly GroupKind[] = ['queue', 'tier', 'language', 'supervisors'];

export interface GroupMember {
  userId: string;
  displayName: string;
  primary: boolean;
}

export interface Group {
  id: string;
  name: string;
  kind: GroupKind;
  overflowGroupId: string | null;
  overflowAfterS: number;
  acceptTimeoutS: number;
  active: boolean;
  members: GroupMember[];
}

export interface GroupBody {
  name: string;
  kind: GroupKind;
  overflowGroupId?: string | null;
  overflowAfterS?: number;
  acceptTimeoutS?: number;
}

export interface GroupPatch extends Partial<GroupBody> {
  active?: boolean;
  members?: { userId: string; primary: boolean }[];
}

export interface Skill {
  code: string;
  label: string;
}

export interface WallboardOperator extends Presence {
  acceptedToday: number;
  expiredToday: number;
  declinedToday: number;
  avgHandleSeconds: number | null;
}

export interface Wallboard {
  at: string;
  queue: {
    total: number;
    bySeverity: Record<Severity, number>;
    oldestQueuedAt: string | null;
    slaBreaches: number;
    offered: number;
    overflowed: number;
  };
  parked: number;
  active: number;
  today: {
    offers: number;
    acceptedWithinSla: number;
    /** Percent (0..100) on the devstack; the console derives it from the counts. */
    acceptRate: number;
    avgAcceptSeconds: number | null;
    closed: number;
    avgHandleSeconds: number | null;
  };
  operators: WallboardOperator[];
  /** Every open event, with owner, target and park. */
  events: EventSummary[];
  storms: { eventId: string; displayNo: string; siteName: string; cameraIds: string[] }[];
  heldSyncChanges: number;
  openWorkItems: number;
  /** Phase 3 follow-up: "today" is since midnight server time. */
  ai?: WallboardAi;
}

/** GET /api/slo: per-segment latency over a window (architecture §5.1), from the alarm hop timestamps. */
export interface SloSegment {
  key: string;
  label: string;
  note: string | null;
  /** p95 target in seconds; null for segments without one (operator response, end to end). */
  targetS: number | null;
  count: number;
  p50: number | null;
  p95: number | null;
  p99: number | null;
  max: number | null;
  overTarget: number;
  status: 'ok' | 'breach' | 'no_data' | 'info';
}

export interface SloReport {
  window: string;
  windowS: number;
  at: string;
  alarms: number;
  perMinute: number;
  segments: SloSegment[];
}

export interface WallboardAi {
  today: { alarms: number; events: number; aiResolved: number; toOperators: number };
  now: { triaging: number; autoResolving: number; pendingApprovals: number; activeWatches: number };
  qaPending: number;
  globalPaused: boolean;
}

/** Reads an offer from GET /api/offers/mine or the SSE message; tolerates `id` for `offerId`. */
export function readOffer(v: unknown): Offer | null {
  if (!isRecord(v)) return null;
  const offerId = typeof v.offerId === 'string' ? v.offerId : typeof v.id === 'string' ? v.id : null;
  const expiresAt = typeof v.expiresAt === 'string' ? v.expiresAt : null;
  if (!offerId || !expiresAt || !isRecord(v.event) || typeof v.event.id !== 'string') return null;
  const pre = isRecord(v.prestage) && Array.isArray(v.prestage.cameraIds) ? v.prestage.cameraIds.filter((x): x is string => typeof x === 'string') : [];
  return { offerId, expiresAt, event: v.event as unknown as EventSummary, prestage: { cameraIds: pre } };
}

export type ReviewStatus = 'new' | 'reviewed' | 'dismissed';
export interface EventAlarm extends AlarmSummary {
  reviewStatus: ReviewStatus;
  lastOccurredAt: string;
  attachedAt: string;
  severity: Severity;
}

export type StepBinding = 'video_review' | 'call' | 'dispatch' | 'note' | 'talk_down' | 'relay' | 'manual';
export const STEP_BINDINGS: readonly StepBinding[] = ['video_review', 'call', 'dispatch', 'note', 'talk_down', 'relay', 'manual'];
export type StepStatus = 'pending' | 'done' | 'na';

export interface MissionStep {
  id: string;
  seq: number;
  title: string;
  hint: string | null;
  required: boolean;
  binding: StepBinding;
  status: StepStatus;
  doneBy: Actor | null;
  doneAt: string | null;
  actionId: string | null;
  note: string | null;
}

export interface CallConditions {
  analytics?: string[];
  window?: { start: string; end: string };
}

export interface CallStep {
  seq: number;
  contactId: string;
  contactName: string;
  jobTitle: string | null;
  phoneId: string;
  phoneType: string | null;
  e164: string | null;
  extension: string | null;
  conditions: CallConditions | null;
  /** Conditions match this event now. */
  applies: boolean;
  /** Contact or phone missing/inactive in the replica. */
  broken: boolean;
}

export type EventActionType =
  | 'video_review'
  | 'call'
  | 'dispatch'
  | 'note'
  | 'bookmark'
  | 'clip_export'
  | 'checklist_manual'
  | 'data_issue'
  /* Phase 5 */
  | 'talk_down'
  | 'relay';
export type ActionStatus = 'requested' | 'succeeded' | 'failed' | 'cancelled';

/** `Action` in the API doc. */
export interface EventAction {
  id: string;
  type: EventActionType;
  actor: Actor;
  status: ActionStatus;
  params: Record<string, unknown>;
  result: Record<string, unknown> | null;
  startedAt: string;
  finishedAt: string | null;
}

export interface LogEntry {
  seq: number;
  at: string;
  actor: Actor;
  kind: string;
  text: string;
  refs: Record<string, unknown>;
}

export interface AuthorizedActivity {
  label: string;
  days?: string[];
  start?: string;
  end?: string;
  note?: string;
  /** anyone: people and vehicles in general are expected (the AI may close ordinary activity); named (default): only the party in the label. */
  covers?: 'anyone' | 'named';
}

export interface EventSite extends SiteSummary {
  specialInstructions: string | null;
  painPoints: string[];
  riskLevel: number | null;
  riskReason: string | null;
  authorizedActivity: AuthorizedActivity[];
  live: boolean;
}

export interface EventCamera extends Camera {
  distancePx: number | null;
  distanceRank: number;
}

export interface EventLink {
  id: string;
  displayNo: string;
  state: EventState;
  closedAt: string | null;
  disposition: string | null;
}

export interface DispositionCode {
  code: string;
  label: string;
  category: string;
  billable: boolean;
  notifiesCustomer: boolean;
  requiresPoliceNumber: boolean;
  requiresNotes: boolean;
  requiresRequiredSteps: boolean;
  active: boolean;
  sortOrder: number;
}

export interface EventAuthority {
  id: string;
  name: string;
  kind: string;
  emergency: string | null;
  nonEmergency: string | null;
  note: string | null;
}

export interface EventDisposition {
  code: string;
  label: string;
  notes: string | null;
  fields: Record<string, unknown>;
  setBy: Actor;
  setAt: string;
  customerReportStatus: string;
}

export interface EventDetail extends EventSummary {
  openedAt: string;
  dispositionedAt: string | null;
  closedAt: string | null;
  handledBy: string | null;
  primary: boolean;
  site: EventSite;
  /** Oldest first. */
  alarms: EventAlarm[];
  /** Sorted nearest-first to the first alarm's camera. */
  cameras: EventCamera[];
  sitemaps: Sitemap[];
  mission: { id: string; name: string; version: number; steps: MissionStep[] } | null;
  callProcedure: CallStep[];
  authority: EventAuthority | null;
  actions: EventAction[];
  log: LogEntry[];
  links: { follows: EventLink | null; splitFrom: EventLink | null; mergedInto: EventLink | null; splits: EventLink[] };
  disposition: EventDisposition | null;
  /** weekly: 12 weeks oldest→newest; recent: last 10 closed. */
  history: { weekly: number[]; recent: { id: string; displayNo: string; closedAt: string; code: string | null; label: string | null }[] };
  dispositionCodes: DispositionCode[];
  /** Video watch time per camera for this event (snake_case in the API doc). */
  watch_summary?: WatchSummaryItem[];
}

export interface WatchSummaryItem {
  cameraId: string;
  cameraName: string | null;
  liveMs: number;
  archiveMs: number;
  views: number;
}

export type CallOutcome = 'answered' | 'voicemail' | 'no_answer' | 'busy' | 'wrong_number';
export const CALL_OUTCOMES: readonly { value: CallOutcome; label: string }[] = [
  { value: 'answered', label: 'Answered' },
  { value: 'voicemail', label: 'Voicemail' },
  { value: 'no_answer', label: 'No answer' },
  { value: 'busy', label: 'Busy' },
  { value: 'wrong_number', label: 'Wrong number' },
];

export type TelephonyProvider = 'ringcentral' | 'simulator' | 'manual';
export const TELEPHONY_PROVIDERS: readonly TelephonyProvider[] = ['ringcentral', 'simulator', 'manual'];

export interface CallStart {
  action: EventAction;
  /** Present on servers with telephony (api-phase1-telephony-audit.md). */
  event?: EventDetail;
  dial: { e164: string | null; extension: string | null };
  mode: TelephonyProvider;
}

export type CallState = 'dialing' | 'ringing' | 'connected' | 'ended' | 'failed';
export const CALL_STATES: readonly CallState[] = ['dialing', 'ringing', 'connected', 'ended', 'failed'];
export type RecordingStatus = 'pending' | 'stored' | 'missing' | 'not_connected';
export const RECORDING_STATUSES: readonly RecordingStatus[] = ['pending', 'stored', 'missing', 'not_connected'];

export interface CallRecording {
  status: RecordingStatus;
  durationSeconds: number | null;
  sha256: string | null;
  contentType: string | null;
  url: string | null;
}

/** `CallResult`: the call Action's `result` (api-phase1-telephony-audit.md). */
export interface CallResult {
  state: CallState;
  provider: TelephonyProvider;
  telephonySessionId: string | null;
  ringingAt: string | null;
  connectedAt: string | null;
  endedAt: string | null;
  talkSeconds: number | null;
  endReason: string | null;
  outcome: CallOutcome | null;
  note: string | null;
  recording: CallRecording | null;
}

function oneOf<T extends string>(v: unknown, list: readonly T[]): T | null {
  return typeof v === 'string' && (list as readonly string[]).includes(v) ? (v as T) : null;
}
function strOrNull(v: unknown): string | null {
  return typeof v === 'string' && v ? v : null;
}
function numOrNull(v: unknown): number | null {
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}

/**
 * Reads a call action's result. Older servers (manual dial only) return `{ outcome, note }` or null:
 * those read as a manual call that is still `dialing` until an outcome is set.
 */
export function callResult(a: EventAction): CallResult {
  const r = a.result ?? {};
  const outcome = oneOf(r.outcome, CALL_OUTCOMES.map((o) => o.value));
  const rec = r.recording;
  let recording: CallRecording | null = null;
  if (typeof rec === 'object' && rec !== null && !Array.isArray(rec)) {
    const o = rec as Record<string, unknown>;
    const status = oneOf(o.status, RECORDING_STATUSES);
    if (status) {
      recording = {
        status,
        durationSeconds: numOrNull(o.durationSeconds),
        sha256: strOrNull(o.sha256),
        contentType: strOrNull(o.contentType),
        url: strOrNull(o.url),
      };
    }
  }
  const state = oneOf(r.state, CALL_STATES) ?? (a.status === 'failed' || a.status === 'cancelled' ? 'failed' : outcome ? 'ended' : 'dialing');
  return {
    state,
    provider: oneOf(r.provider, TELEPHONY_PROVIDERS) ?? 'manual',
    telephonySessionId: strOrNull(r.telephonySessionId),
    ringingAt: strOrNull(r.ringingAt),
    connectedAt: strOrNull(r.connectedAt),
    endedAt: strOrNull(r.endedAt),
    talkSeconds: numOrNull(r.talkSeconds),
    endReason: strOrNull(r.endReason),
    outcome,
    note: strOrNull(r.note),
    recording,
  };
}

/** The call is still in progress (not ended or failed). */
export function callIsLive(r: CallResult): boolean {
  return r.state === 'dialing' || r.state === 'ringing' || r.state === 'connected';
}

/* ---------- event audit (GET /api/events/:id/audit) ---------- */

export interface AuditEntry {
  id: string;
  at: string;
  actor: { type: string; id: string | null; name: string | null };
  action: string;
  text: string | null;
  data: Record<string, unknown>;
}

/* ---------- setup users (GET/PATCH /api/setup/users) ---------- */

export interface SetupUser {
  id: string;
  upn: string;
  displayName: string;
  status: 'active' | 'disabled' | string;
  roles: Role[];
  rcExtensionId: string | null;
  lastLoginAt: string | null;
  /** Phase 2: skill codes. */
  skills?: string[];
  /** Phase 2: group memberships. */
  groups?: PresenceGroupRef[];
}

export interface SetupUserPatch {
  roles?: Role[];
  rcExtensionId?: string | null;
  status?: 'active' | 'disabled';
  skills?: string[];
}

export interface DispatchBody {
  authorityId: string | null;
  agencyName: string;
  agencyIncidentNumber: string | null;
  callType: string;
  subjectDescription: string;
  note?: string;
}

export interface DispositionBody {
  code: string;
  fields?: Record<string, string>;
  notes?: string;
  customerReport: boolean;
}

/** 422 details from POST /disposition. */
export interface DispositionGuardDetails {
  missingSteps: string[];
  missingFields: string[];
  /** Phase 4: summaries of agent requests still waiting for an answer. */
  pendingApprovals: string[];
}

export interface DataIssueBody {
  entityType: string;
  entityId: string;
  subItem?: { kind: 'phone' | 'email' | 'zone'; id: string };
  field?: string;
  issueType: string;
  severity: string;
  description: string;
  suggestedValue?: string;
}

export interface FeaturesConfig {
  agent: boolean;
  park: boolean;
  transfer: boolean;
  deterrence: boolean;
  multiEvent: boolean;
  /** RingCentral Phase 1b: the browser softphone and its call panel (comms.softphone). */
  softphone: boolean;
}

/* ------------------------------------------------------------------ */
/* Phase 1b: the softphone (docs/ringcentral/SOFTPHONE.md)             */
/* ------------------------------------------------------------------ */

/** The workstation an agent sits at (docs/ringcentral/WORKSTATIONS.md): its phone is theirs while seated. */
export interface WorkstationSeat {
  workstationId: string;
  label: string;
  extensionId: string | null;
  since: string;
  /** 'device': recognised from this PC; 'manual': picked in the console. */
  method: 'manual' | 'device';
}

/** The PC this browser runs on, recognised by its device certificate, and the desk it is assigned to. */
export interface WorkstationHere {
  deviceId: string;
  deviceName: string | null;
  blocked: boolean;
  workstationId: string | null;
  label: string | null;
}

/** POST /api/workstations/here: what recognising this PC did. */
export interface WorkstationHereResult {
  outcome: 'unrecognised' | 'unassigned' | 'blocked' | 'seated' | 'already' | 'refused';
  reason?: string;
  here: WorkstationHere | null;
  seat: WorkstationSeat | null;
}

/** A PC the SOC has seen (Setup → Workstations, admins). */
export interface WorkstationDevice {
  id: string;
  name: string | null;
  workstation: { id: string; label: string } | null;
  assignedAt: string | null;
  blocked: boolean;
  certExpires: string | null;
  firstSeenAt: string;
  lastSeenAt: string;
  lastSeenBy: string | null;
}

export interface Workstation {
  id: string;
  label: string;
  extensionId: string | null;
  active: boolean;
  seated: { userId: string; name: string; since: string } | null;
  mine: boolean;
  /** The desk has its own PC: it is taken from that PC, not picked. */
  ownComputer: boolean;
  /** Admins only: the PC assigned to it. */
  device?: { id: string; name: string | null } | null;
  /** Admins only: the workstation's RingCentral sign-in is in place. */
  signedIn?: boolean;
  signedInAt?: string | null;
}

export interface SoftphoneConfigResponse {
  enabled: boolean;
  engine: 'simulator' | 'ringcentral';
  simulated: boolean;
  /** The workstation this agent sits at, or null (their own extension, if they have one). */
  seat: WorkstationSeat | null;
  signedIn: boolean;
  extensionId: string | null;
  /** RingCentral: the operator has an extension in the SOC (Setup → Users); without one they can't sign in. */
  hasExtension: boolean;
  /** The SOC numbers this operator may present as caller ID. */
  callerIds: string[];
}

/** One of my calls, or a missed call (GET /api/communications/calls). Numbers: last four digits. */
export interface CommCall {
  id: string;
  direction: 'inbound' | 'outbound';
  status: 'initiated' | 'ringing' | 'answered' | 'ended' | 'missed' | 'failed';
  simulated: boolean;
  remoteLast4: string | null;
  contactId: string | null;
  contactName: string | null;
  agentName: string | null;
  startedAt: string;
  answeredAt: string | null;
  endedAt: string | null;
  talkSeconds: number | null;
}

/** The screen pop for a call offered to the phone (GET /api/communications/:id/screen-pop); the parts the panel shows. */
export interface ScreenPop {
  communication: { id: string; status: string; simulated: boolean; remoteLast4: string | null; phoneNumber: string | null };
  caller: { label: string; identified: boolean; verified: boolean };
  workflow: 'verified' | 'verify' | 'choose' | 'identify';
  candidates: {
    kind: 'person' | 'organization' | 'site';
    name: string | null;
    companyName: string | null;
    sites: { siteId: string; name: string | null; roles: string[] }[];
    openEvents: { eventId: string; displayNo: string; severity: string; state: string; ownerName: string | null }[];
  }[];
  sites: { siteId: string; name: string | null; specialInstructions: string | null }[];
  lastOutboundCall: { at?: string; agentName?: string | null; eventId?: string | null } | null;
}

/* ------------------------------------------------------------------ */
/* Phase 1: setup, exports, audit                                       */
/* ------------------------------------------------------------------ */

export interface TemplateStep {
  seq: number;
  title: string;
  hint: string | null;
  required: boolean;
  binding: StepBinding;
}

export interface MissionTemplate {
  id: string;
  name: string;
  vertical: string | null;
  eventTypes: string[];
  version: number;
  steps: TemplateStep[];
  siteCount: number;
  updatedAt: string;
}

export interface MissionTemplateBody {
  name: string;
  vertical: string | null;
  eventTypes: string[];
  steps: TemplateStep[];
}

export interface Correlation {
  window_s: number;
  reopen_s: number;
  burst_s: number;
  storm_rate: number;
}

export interface ScheduleOverride {
  id: string;
  from: string;
  to: string;
  mode: 'monitor' | 'suppress';
  reason: string;
}

export interface SiteOps {
  specialInstructions: string | null;
  painPoints: string[];
  riskLevel: number | null;
  riskReason: string | null;
  authorizedActivity: AuthorizedActivity[];
  customerTier: string | null;
  humanReviewOnly: boolean;
  /** The agent may resolve an event with an alarm the EyeQ VMS's AI verified as authorized (never clear). Off by default. */
  vmsVerifiedAutoResolve?: boolean;
  correlation: Correlation;
  maintenanceUntil: string | null;
  scheduleOverrides: ScheduleOverride[];
  /** Phase 2: skills an operator needs to be offered this site's events. */
  requiredSkills?: string[];
}

export interface ReadinessCheck {
  key: string;
  label: string;
  ok: boolean;
  detail: string | null;
}

export type AuthorityKind = 'police' | 'sheriff' | 'fire' | 'ems' | 'other';
export const AUTHORITY_KINDS: readonly AuthorityKind[] = ['police', 'sheriff', 'fire', 'ems', 'other'];

export type Jurisdiction =
  | { type: 'Polygon'; coordinates: number[][][] }
  | { type: 'MultiPolygon'; coordinates: number[][][][] };

export interface Authority {
  id: string;
  name: string;
  kind: AuthorityKind;
  emergency: string | null;
  nonEmergency: string | null;
  note: string | null;
  /** GeoJSON lon/lat. */
  jurisdiction: Jurisdiction | null;
  /** Explicit assignments win over geo. */
  siteIds: string[];
  active: boolean;
}

export interface SiteSetup {
  site: SiteSummary;
  ops: SiteOps;
  live: boolean;
  goLiveAt: string | null;
  missions: { eventType: string; templateId: string; templateName: string; version: number }[];
  callProcedure: CallStep[];
  authority: Authority | null;
  readiness: { ok: boolean; checks: ReadinessCheck[] };
  contacts: Contact[];
}

export interface CallProcedureStepBody {
  contactId: string;
  phoneId: string;
  conditions: CallConditions | null;
}

export interface AuditRow {
  id: string;
  at: string;
  actor: Actor;
  action: string;
  subjectType: string;
  subjectId: string;
  siteId: string | null;
  data: Json;
}

export interface AuditQuery {
  subjectType?: string;
  subjectId?: string;
  siteId?: string;
  actorId?: string;
  action?: string;
  from?: string;
  to?: string;
  limit?: number;
}

export interface AuditVerify {
  ok: boolean;
  rows: number;
  /** Chains the day has (one per shard). */
  shards?: number;
  brokenAt?: string | null;
}

/* ------------------------------------------------------------------ */
/* Realtime                                                             */
/* ------------------------------------------------------------------ */

export interface StreamEventMap {
  alarm: AlarmSummary;
  replica: { entityType: string; entityId: string; version: number; effect: string };
  import: { id: string; status: ImportStatus };
  hold: { id: string; status: string };
  health: { cameraId: string; status: HealthStatus; changedAt: string };
  ping: Record<string, never>;
  event: EventSummary;
  queue: { count: number };
  /** Phase 2: to the offered user only. Read it with readOffer(). */
  offer: unknown;
  offer_withdrawn: OfferWithdrawn;
  raise: RaiseMessage;
  /** Phase 3 follow-up: after every agent tool call, `{ eventId }` only. Refetch the panel. */
  agent: { eventId: string };
  /** Phase 4: the agent answered an instruction (to the operator). */
  agent_reply: { eventId: string; instructionId: string | null; text: string };
  /** Phase 4: the agent asks the owner to OK an action (20 s). */
  approval: ApprovalMessage;
  /** Phase 4: a watch hit on an event that isn't parked (to the owner). */
  watch_hit: WatchHitRef & { eventId: string };
  /** Phase 5: a speaker or relay changed state (everyone). */
  device: DeviceStateMessage;
  /** Phase 1b: my phone (the simulator's calls changed, or a hang-up asked for from another screen). Forwarded to the phone as is. */
  softphone: unknown;
}
export type StreamEventName = keyof StreamEventMap;
export const STREAM_EVENTS: readonly StreamEventName[] = [
  'alarm',
  'replica',
  'import',
  'hold',
  'health',
  'ping',
  'event',
  'queue',
  'offer',
  'offer_withdrawn',
  'raise',
  'agent',
  'agent_reply',
  'approval',
  'watch_hit',
  'device',
  'softphone',
];

/* ------------------------------------------------------------------ */
/* Transport                                                            */
/* ------------------------------------------------------------------ */

export class ApiError extends Error {
  readonly status: number;
  readonly details: Json | undefined;
  constructor(status: number, message: string, details?: Json) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.details = details;
  }
}

/** Human text for any thrown value; API errors carry the server's `error` field. */
export function errorText(e: unknown): string {
  if (e instanceof ApiError) return e.status ? `${e.message} (HTTP ${e.status})` : e.message;
  if (e instanceof Error) return e.message;
  return String(e);
}

let auth: AuthProvider | null = null;
/** The last auth headers handed out, for requests that must be sent synchronously (pagehide). */
let lastHeaders: Record<string, string> = {};
export function setAuthProvider(provider: AuthProvider): void {
  auth = provider;
  lastHeaders = {};
}
async function authHeaders(): Promise<Record<string, string>> {
  const h = auth ? await auth.headers() : {};
  lastHeaders = h;
  return h;
}

/**
 * POSTs JSON with `fetch(…, { keepalive: true })` so it survives the page being hidden or unloaded.
 * Runs synchronously (no await before fetch) with the most recent auth headers, so it works inside
 * `pagehide` / `visibilitychange` handlers. Errors are ignored: this is best-effort reporting.
 */
export function postKeepalive(path: string, body: unknown): void {
  try {
    void fetch(path, {
      method: 'POST',
      keepalive: true,
      headers: { ...lastHeaders, accept: 'application/json', 'content-type': 'application/json' },
      body: JSON.stringify(body),
    }).catch(() => undefined);
  } catch {
    /* keepalive quota exceeded or fetch unavailable */
  }
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

async function toApiError(res: Response): Promise<ApiError> {
  let message = res.statusText || `Request failed`;
  let details: Json | undefined;
  const text = await res.text().catch(() => '');
  if (text) {
    try {
      const body: unknown = JSON.parse(text);
      if (isRecord(body) && typeof body.error === 'string') {
        message = body.error;
        details = body.details as Json | undefined;
      } else {
        message = text.slice(0, 300);
      }
    } catch {
      message = text.slice(0, 300);
    }
  }
  return new ApiError(res.status, message, details);
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  /** Serialized as JSON. */
  json?: unknown;
  /** Sent as-is (used for the import file upload). */
  rawBody?: string;
  /** Binary body sent as-is with its own content type (Phase 5: the push-to-talk clip). */
  blob?: Blob;
  headers?: Record<string, string>;
  signal?: AbortSignal;
}

async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const headers: Record<string, string> = { accept: 'application/json', ...(await authHeaders()), ...opts.headers };
  let body: string | Blob | undefined;
  if (opts.json !== undefined) {
    headers['content-type'] = 'application/json';
    body = JSON.stringify(opts.json);
  } else if (opts.rawBody !== undefined) {
    headers['content-type'] ??= 'application/json';
    body = opts.rawBody;
  } else if (opts.blob !== undefined) {
    headers['content-type'] ??= opts.blob.type || 'application/octet-stream';
    body = opts.blob;
  }
  let res: Response;
  try {
    res = await fetch(path, { method: opts.method ?? 'GET', headers, body, signal: opts.signal });
  } catch (e) {
    if (e instanceof DOMException && e.name === 'AbortError') throw e;
    throw new ApiError(0, `Network error: cannot reach the API (${e instanceof Error ? e.message : String(e)})`);
  }
  if (!res.ok) throw await toApiError(res);
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  return (text ? JSON.parse(text) : undefined) as T;
}

function qs(params: Record<string, string | number | null | undefined>): string {
  const u = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v !== undefined && v !== null && v !== '') u.set(k, String(v));
  const s = u.toString();
  return s ? `?${s}` : '';
}

const enc = encodeURIComponent;

/** POST /api/events/:id/<path> with expectedVersion (409 on a stale version). */
function eventCommand<T>(id: string, path: string, body: object, expectedVersion: number): Promise<T> {
  return request<T>(`/api/events/${enc(id)}/${path}`, { method: 'POST', json: { ...body, expectedVersion } });
}

/** True for the 409 a stale expectedVersion (or a lost race) returns. */
export function isConflict(e: unknown): e is ApiError {
  return e instanceof ApiError && e.status === 409;
}

function strList(v: Json | undefined): string[] {
  return Array.isArray(v) ? v.map((x) => (typeof x === 'string' ? x : JSON.stringify(x))) : [];
}

/** Reads the 422 guard details from a failed disposition. */
export function dispositionGuards(e: unknown): DispositionGuardDetails | null {
  if (!(e instanceof ApiError) || e.status !== 422) return null;
  const d = e.details;
  if (typeof d !== 'object' || d === null || Array.isArray(d)) return { missingSteps: [], missingFields: [], pendingApprovals: [] };
  return { missingSteps: strList(d.missingSteps), missingFields: strList(d.missingFields), pendingApprovals: strList(d.pendingApprovals) };
}

/* ------------------------------------------------------------------ */
/* Authenticated binary fetches (images, clips, .eml)                   */
/* ------------------------------------------------------------------ */

const blobCache = new Map<string, Promise<string>>();
const BLOB_CACHE_MAX = 400;

/** Fetches an authenticated URL and returns an object URL. Results are cached per URL. */
export function fetchBlobUrl(url: string, opts: { cache?: boolean } = {}): Promise<string> {
  const useCache = opts.cache !== false;
  const hit = useCache ? blobCache.get(url) : undefined;
  if (hit) return hit;
  const p = (async () => {
    const res = await fetch(url, { headers: await authHeaders() });
    if (!res.ok) throw await toApiError(res);
    return URL.createObjectURL(await res.blob());
  })();
  if (useCache) {
    blobCache.set(url, p);
    p.catch(() => blobCache.delete(url));
    if (blobCache.size > BLOB_CACHE_MAX) {
      const oldest = blobCache.keys().next().value;
      if (oldest !== undefined) {
        const old = blobCache.get(oldest);
        blobCache.delete(oldest);
        old?.then((u) => URL.revokeObjectURL(u)).catch(() => undefined);
      }
    }
  }
  return p;
}

/**
 * POSTs JSON and returns the binary answer as an object URL (Phase 5: talk-down preview audio). Cached per body, so a
 * message previewed twice is fetched once; the server caches the TTS by text too.
 */
const previewCache = new Map<string, Promise<string>>();
export function postForBlobUrl(path: string, json: unknown): Promise<string> {
  const key = `${path} ${JSON.stringify(json)}`;
  const hit = previewCache.get(key);
  if (hit) return hit;
  const p = (async () => {
    const res = await fetch(path, { method: 'POST', headers: { ...(await authHeaders()), 'content-type': 'application/json' }, body: JSON.stringify(json) });
    if (!res.ok) throw await toApiError(res);
    return URL.createObjectURL(await res.blob());
  })();
  previewCache.set(key, p);
  p.catch(() => previewCache.delete(key));
  if (previewCache.size > 60) {
    const oldest = previewCache.keys().next().value;
    if (oldest !== undefined) {
      const old = previewCache.get(oldest);
      previewCache.delete(oldest);
      old?.then((u) => URL.revokeObjectURL(u)).catch(() => undefined);
    }
  }
  return p;
}

/** Downloads an authenticated URL as a file (used for raw .eml and media exports). */
export async function downloadAuthed(url: string, fileName: string): Promise<void> {
  const objectUrl = await fetchBlobUrl(url, { cache: false });
  const a = document.createElement('a');
  a.href = objectUrl;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(objectUrl), 30_000);
}

/* ------------------------------------------------------------------ */
/* Endpoints                                                            */
/* ------------------------------------------------------------------ */

export const api = {
  me: (signal?: AbortSignal) => request<Me>('/api/me', { signal }),

  // master data
  companies: (signal?: AbortSignal) => request<Company[]>('/api/master/companies', { signal }),
  sites: (companyId?: string, signal?: AbortSignal) =>
    request<SiteSummary[]>(`/api/master/sites${qs({ companyId })}`, { signal }),
  site: (id: string, signal?: AbortSignal) => request<SiteDetail>(`/api/master/sites/${enc(id)}`, { signal }),

  // alarms
  // flag: only alarms with that flag (received_paused: what came in while the site's alarms were paused)
  alarms: (p: { limit?: number; before?: string; siteId?: string; flag?: string }, signal?: AbortSignal) =>
    request<AlarmSummary[]>(`/api/alarms${qs({ limit: p.limit ?? 100, before: p.before, siteId: p.siteId, flag: p.flag })}`, { signal }),
  alarm: (id: string, signal?: AbortSignal) => request<AlarmDetail>(`/api/alarms/${enc(id)}`, { signal }),

  // video (eventId: the session also records a video_review action on that event;
  // mode 'alarm': the player shows the alarm clip, so the watch-time view is recorded as alarm, not archive)
  // prestage: a session opened hidden while an offer is showing (api-phase2.md pre-staging); sent as
  // ?prestage=1 so the server can tell these views apart (older servers ignore it).
  // hevc: this browser plays H.265 itself, so an H.265 camera needs no transcode (playsHevc()).
  // secondary: NX's low-resolution stream, for a quad tile that isn't the main camera.
  // The stream URL can be on another origin (core's MEDIA_ORIGIN): video then has its own connection.
  videoSession: (cameraId: string, at: string | null, signal?: AbortSignal, eventId?: string | null, mode?: 'alarm' | null, prestage?: boolean, secondary?: boolean) =>
    request<StreamDescriptor>(`/api/video/session${qs({ cameraId, at, eventId, mode, prestage: prestage ? 1 : null, hevc: playsHevc() ? 1 : null, secondary: secondary ? 1 : null })}`, { signal }),
  thumbnailUrl: (cameraId: string, at?: string | null) => `/api/video/thumbnail${qs({ cameraId, at })}`,
  // the timeline: recorded periods for a camera lane (null: no archive, simulator), and a small frame for scrubbing
  footage: (cameraId: string, from: string, to: string, signal?: AbortSignal) =>
    request<{ periods: { start: string; end: string }[] | null }>(`/api/video/footage${qs({ cameraId, from, to })}`, { signal }),
  previewUrl: (cameraId: string, at: string) => `/api/video/preview${qs({ cameraId, at })}`,
  // watch time: watchedMs is time the video was playing and visible since the last report
  /** A fresh stream URL for an open recorded view, so its continuous playback outlasts the first URL (continuous.ts). */
  videoTicket: (viewId: string, secondary?: boolean) =>
    request<{ url: string; expiresAt: string }>(`/api/video/views/${enc(viewId)}/ticket${qs({ hevc: playsHevc() ? 1 : null, secondary: secondary ? 1 : null })}`),
  viewHeartbeat: (viewId: string, watchedMs: number) =>
    request<{ ok: boolean }>(`/api/video/views/${enc(viewId)}/heartbeat`, { method: 'POST', json: { watchedMs } }),
  viewEnd: (viewId: string, watchedMs: number, reason: ViewEndReason) =>
    request<{ ok: boolean; totalWatchedMs?: number }>(`/api/video/views/${enc(viewId)}/end`, { method: 'POST', json: { watchedMs, reason } }),
  /** viewEnd for pagehide / visibilitychange: keepalive fetch, fire and forget. */
  viewEndKeepalive: (viewId: string, watchedMs: number, reason: ViewEndReason) =>
    postKeepalive(`/api/video/views/${enc(viewId)}/end`, { watchedMs, reason }),

  // queue and events (Phase 1). Every command carries the event version the client last saw.
  queue: (signal?: AbortSignal) => request<EventSummary[]>('/api/queue', { signal }),
  myEvents: (signal?: AbortSignal) => request<EventSummary[]>('/api/events/mine', { signal }),
  events: (p: { state?: 'open' | 'closed' | 'all'; siteId?: string; limit?: number; before?: string }, signal?: AbortSignal) =>
    request<EventSummary[]>(`/api/events${qs({ state: p.state, siteId: p.siteId, limit: p.limit, before: p.before })}`, { signal }),
  event: (id: string, signal?: AbortSignal) => request<EventDetail>(`/api/events/${enc(id)}`, { signal }),
  claim: (id: string, expectedVersion: number) => eventCommand<EventDetail>(id, 'claim', {}, expectedVersion),
  take: (id: string, expectedVersion: number) => eventCommand<EventDetail>(id, 'take', {}, expectedVersion),
  reviewAlarm: (id: string, alarmId: string, status: ReviewStatus, expectedVersion: number) =>
    eventCommand<EventDetail>(id, `alarms/${enc(alarmId)}/review`, { status }, expectedVersion),
  split: (id: string, alarmIds: string[], claim: boolean, expectedVersion: number) =>
    eventCommand<{ event: EventDetail; newEvent: EventDetail }>(id, 'split', { alarmIds, claim }, expectedVersion),
  merge: (id: string, otherEventId: string, expectedVersion: number) =>
    eventCommand<EventDetail>(id, 'merge', { otherEventId }, expectedVersion),
  addNote: (id: string, text: string, expectedVersion: number) => eventCommand<EventDetail>(id, 'notes', { text }, expectedVersion),
  startCall: (id: string, body: { contactId: string; phoneId: string }, expectedVersion: number) =>
    eventCommand<CallStart>(id, 'calls', body, expectedVersion),
  callOutcome: (id: string, actionId: string, body: { outcome: CallOutcome; note?: string }, expectedVersion: number) =>
    eventCommand<EventDetail>(id, `calls/${enc(actionId)}/outcome`, body, expectedVersion),
  /** RingCentral: links the widget's telephony session to the call action. */
  callSession: (id: string, actionId: string, telephonySessionId: string) =>
    request<EventDetail>(`/api/events/${enc(id)}/calls/${enc(actionId)}/session`, { method: 'POST', json: { telephonySessionId } }),
  callHangup: (id: string, actionId: string, expectedVersion: number) =>
    eventCommand<EventDetail>(id, `calls/${enc(actionId)}/hangup`, {}, expectedVersion),
  /** RingCentral: the phone couldn't place the call, so it fails now instead of showing dialing. */
  callNotPlaced: (id: string, actionId: string, error: string, expectedVersion: number) =>
    eventCommand<EventDetail>(id, `calls/${enc(actionId)}/not-placed`, { error }, expectedVersion),
  softphoneConfig: (signal?: AbortSignal) => request<SoftphoneConfigResponse>('/api/softphone', { signal }),
  workstations: (all = false, signal?: AbortSignal) =>
    request<{ workstations: Workstation[]; seat: WorkstationSeat | null; here: WorkstationHere | null; recognising: boolean }>(`/api/workstations${all ? '?all=true' : ''}`, { signal }),
  workstationHere: () => request<WorkstationHereResult>('/api/workstations/here', { method: 'POST', json: {} }),
  workstationDevices: (signal?: AbortSignal) => request<{ recognising: boolean; here: WorkstationHere | null; devices: WorkstationDevice[] }>('/api/workstations/devices', { signal }),
  patchWorkstationDevice: (id: string, body: { workstationId?: string | null; blocked?: boolean }) => request<{ ok: boolean }>(`/api/workstations/devices/${enc(id)}`, { method: 'PATCH', json: body }),
  createWorkstation: (body: { label: string; extensionId?: string | null }) => request<Workstation>('/api/workstations', { method: 'POST', json: body }),
  patchWorkstation: (id: string, body: { label?: string; extensionId?: string | null; active?: boolean }) => request<Workstation>(`/api/workstations/${enc(id)}`, { method: 'PATCH', json: body }),
  claimWorkstation: (id: string) => request<{ seat: WorkstationSeat | null }>(`/api/workstations/${enc(id)}/claim`, { method: 'POST', json: {} }),
  leaveWorkstation: () => request<{ seat: null }>('/api/workstations/release', { method: 'POST', json: {} }),
  /** Supervisors: release someone's seat (moving them), with a reason. */
  releaseWorkstation: (id: string, reason: string) => request<{ released: boolean }>(`/api/workstations/${enc(id)}/release`, { method: 'POST', json: { reason } }),
  /** Admins: sign a workstation in to RingCentral as the workstation's user (the pop-up flow, like the agent's own). */
  workstationRcLogin: (id: string) => request<{ authorizeUrl: string }>(`/api/workstations/${enc(id)}/rc-login`, { method: 'POST', json: {} }),
  workstationRcLogout: (id: string) => request<{ signedIn: boolean }>(`/api/workstations/${enc(id)}/rc-logout`, { method: 'POST', json: {} }),
  /** A call answered on the workstation's desk phone, controlled through the server (Call Control). Recorded as the desk phone's. */
  deskControl: (communicationId: string, action: 'hold' | 'unhold' | 'hangup' | 'transfer' | 'reject', to?: string) =>
    request<{ ok: boolean }>(`/api/softphone/desk/${enc(communicationId)}/${action}`, { method: 'POST', json: to ? { to } : {} }),
  /** Close a call of yours that no phone here holds (never placed, or the provider no longer has it); 409 when it's live on another phone. */
  closeServerCall: (communicationId: string) => request<{ ok: boolean }>(`/api/softphone/calls/${enc(communicationId)}/close`, { method: 'POST', json: {} }),
  /** Simulator only: the workstation's desk phone answers a ringing call. */
  simAnswerOnDeskPhone: (telephonySessionId: string) => request<unknown>(`/api/softphone/sim/calls/${enc(telephonySessionId)}/answer-on-desk-phone`, { method: 'POST', json: {} }),
  /** The phone's own server calls (telephony/hub.ts), made with this tab's sign-in. Only /api/softphone paths. */
  softphoneHttp: (method: 'GET' | 'POST', path: string, body?: unknown) => {
    if (!/^\/api\/softphone(\/|$)/.test(path) || path.includes('..')) return Promise.reject(new ApiError(0, `the phone may not call ${path}`));
    return request<unknown>(path, { method, json: body === undefined && method === 'POST' ? {} : body });
  },
  softphoneLogin: () => request<{ authorizeUrl: string }>('/api/softphone/login', { method: 'POST', json: {} }),
  softphoneLoginComplete: (code: string, state: string) => request<{ signedIn: boolean; extensionId: string }>('/api/softphone/login/complete', { method: 'POST', json: { code, state } }),
  softphoneLogout: () => request<{ signedIn: boolean }>('/api/softphone/logout', { method: 'POST', json: {} }),
  /** The number of a call I was on, or of a missed call, to call back. Audited as communication.number_revealed. */
  softphoneRedial: (communicationId: string) => request<{ to: string }>('/api/softphone/redial', { method: 'POST', json: { communicationId } }),
  /** Simulator only: a simulated caller rings my phone. */
  softphoneTestCall: (from?: string) => request<{ telephonySessionId: string }>('/api/softphone/sim/ring', { method: 'POST', json: from ? { from } : {} }),
  softphoneSession: (telephonySessionId: string, signal?: AbortSignal) =>
    request<{ communicationId: string; status: string }>(`/api/softphone/sessions/${enc(telephonySessionId)}`, { signal }),
  screenPop: (communicationId: string, signal?: AbortSignal) => request<ScreenPop>(`/api/communications/${enc(communicationId)}/screen-pop`, { signal }),
  /** My calls (mine) or missed calls anyone may return (missed), last 7 days. */
  phoneCalls: (which: 'mine' | 'missed', signal?: AbortSignal) =>
    request<{ calls: CommCall[] }>(`/api/communications/calls${qs({ [which]: 'true', days: 7 })}`, { signal }),
  /** The call recording (audio). Each fetch is audited as call.recording_played. */
  callRecordingUrl: (id: string, actionId: string) => `/api/events/${enc(id)}/calls/${enc(actionId)}/recording`,
  eventAudit: (id: string, signal?: AbortSignal) => request<AuditEntry[]>(`/api/events/${enc(id)}/audit`, { signal }),
  dispatch: (id: string, body: DispatchBody, expectedVersion: number) => eventCommand<EventDetail>(id, 'dispatch', body, expectedVersion),
  bookmark: (id: string, body: { cameraId: string; at: string; note?: string }, expectedVersion: number) =>
    eventCommand<EventDetail>(id, 'bookmarks', body, expectedVersion),
  clip: (id: string, body: { cameraId: string; from: string; to: string }, expectedVersion: number) =>
    eventCommand<EventDetail>(id, 'clips', body, expectedVersion),
  missionStep: (id: string, stepId: string, body: { status: StepStatus; note?: string }, expectedVersion: number) =>
    eventCommand<EventDetail>(id, `mission/${enc(stepId)}`, body, expectedVersion),
  disposition: (id: string, body: DispositionBody, expectedVersion: number) =>
    eventCommand<EventDetail>(id, 'disposition', body, expectedVersion),
  dataIssue: (id: string, body: DataIssueBody, expectedVersion: number) =>
    eventCommand<EventDetail>(id, 'data-issues', body, expectedVersion),
  severity: (id: string, body: { severity: Severity; reason: string }, expectedVersion: number) =>
    eventCommand<EventDetail>(id, 'severity', body, expectedVersion),

  // Phase 2: presence, offers, park, transfer, wallboard (api-phase2.md)
  presenceMe: (signal?: AbortSignal) => request<Presence>('/api/presence/me', { signal }),
  setPresence: (status: SettablePresence) => request<Presence>('/api/presence/me', { method: 'PUT', json: { status } }),
  presenceAll: (signal?: AbortSignal) => request<Presence[]>('/api/presence', { signal }),
  setRouting: (userId: string, paused: boolean, reason?: string) =>
    request<Presence>(`/api/presence/${enc(userId)}/routing`, { method: 'POST', json: reason ? { paused, reason } : { paused } }),
  offerMine: (signal?: AbortSignal) => request<unknown>('/api/offers/mine', { signal }).then(readOffer),
  acceptOffer: (offerId: string) => request<EventDetail>(`/api/offers/${enc(offerId)}/accept`, { method: 'POST', json: {} }),
  declineOffer: (offerId: string, reason?: string) =>
    request<{ ok: boolean }>(`/api/offers/${enc(offerId)}/decline`, { method: 'POST', json: reason ? { reason } : {} }),
  park: (id: string, body: ParkBody, expectedVersion: number) => eventCommand<EventDetail>(id, 'park', body, expectedVersion),
  resume: (id: string, expectedVersion: number) => eventCommand<EventDetail>(id, 'resume', {}, expectedVersion),
  /** Supervisor force-raise of a parked event. */
  raise: (id: string, reason: string) => request<EventDetail>(`/api/events/${enc(id)}/raise`, { method: 'POST', json: { reason } }),
  transfer: (id: string, body: TransferBody, expectedVersion: number) => eventCommand<EventSummary>(id, 'transfer', body, expectedVersion),
  /** Supervisor: makes it the user's active event directly, even over their cap. */
  reassign: (id: string, toUserId: string, expectedVersion: number) => eventCommand<EventSummary>(id, 'reassign', { toUserId }, expectedVersion),
  wallboard: (signal?: AbortSignal) => request<Wallboard>('/api/wallboard', { signal }),
  slo: (window: string, signal?: AbortSignal) => request<SloReport>(`/api/slo${qs({ window })}`, { signal }),
  /** The first video frame shown after accepting (architecture §5.1 first_frame_at). The server stamps it once. */
  firstFrame: (eventId: string, cameraId: string, prestaged: boolean) =>
    request<{ stamped: number }>(`/api/events/${enc(eventId)}/first-frame`, { method: 'POST', json: { cameraId, prestaged } }),
  groups: (signal?: AbortSignal) => request<Group[]>('/api/setup/groups', { signal }),
  createGroup: (body: GroupBody) => request<Group>('/api/setup/groups', { method: 'POST', json: body }),
  patchGroup: (id: string, body: GroupPatch) => request<Group>(`/api/setup/groups/${enc(id)}`, { method: 'PATCH', json: body }),
  skills: (signal?: AbortSignal) => request<Skill[]>('/api/setup/skills', { signal }),
  createSkill: (body: Skill) => request<Skill>('/api/setup/skills', { method: 'POST', json: body }),

  aiRules: (siteId: string | null, signal?: AbortSignal) => request<AiRulesSetup>(`/api/setup/ai/rules${qs({ siteId })}`, { signal }),
  putAiRules: (siteId: string | null, rules: AiRule[], expectedVersion: number, note: string) =>
    request<Pick<AiRulesSetup, 'ruleSet' | 'history'>>('/api/setup/ai/rules', { method: 'PUT', json: { siteId, rules, expectedVersion, note } }),
  inheritAiRules: (siteId: string, expectedVersion: number) =>
    request<Pick<AiRulesSetup, 'ruleSet' | 'history'>>(`/api/setup/ai/rules${qs({ siteId, expectedVersion })}`, { method: 'DELETE' }),
  testAiRules: (siteId: string | null, rules: AiRule[]) => request<AiRulesTest>('/api/setup/ai/rules/test', { method: 'POST', json: { siteId, rules } }),

  // Phase 3: the AI agent (api-phase3.md)
  /** Not audited per read (the tool calls are). Operators at a Level 0 site get `{ visible: false }`. */
  agentPanel: (id: string, signal?: AbortSignal) => request<AgentPanel>(`/api/events/${enc(id)}/agent`, { signal }),
  pauseAgent: (id: string, reason?: string) =>
    request<EventSummary>(`/api/events/${enc(id)}/agent/pause`, { method: 'POST', json: reason ? { reason } : {} }),
  resumeAgent: (id: string) => request<EventSummary>(`/api/events/${enc(id)}/agent/resume`, { method: 'POST', json: {} }),
  /** Supervisor: global or site pause. Returns the effective policy. */
  setAgentPaused: (body: { scope: 'global' | 'site'; siteId?: string; paused: boolean; reason?: string }) =>
    request<AiPolicy>('/api/agent/pause', { method: 'POST', json: body }),
  aiSetup: (signal?: AbortSignal) => request<AiSetup>('/api/setup/ai', { signal }),
  putAiGlobal: (body: AiPolicyPatch) => request<AiPolicy>('/api/setup/ai', { method: 'PUT', json: body }),
  putAiModel: (modelConfigId: string) => request<ModelConfig[]>('/api/setup/ai/model', { method: 'PUT', json: { modelConfigId } }),
  addAiModel: (body: NewModelConfig) => request<ModelConfig[]>('/api/setup/ai/models', { method: 'POST', json: body }),
  siteAi: (id: string, signal?: AbortSignal) => request<SiteAiPolicy>(`/api/setup/sites/${enc(id)}/ai`, { signal }),
  putSiteAi: (id: string, body: AiPolicyPatch) => request<AiPolicy>(`/api/setup/sites/${enc(id)}/ai`, { method: 'PUT', json: body }),
  /** Back to the global policy. 409 when that would raise the live level without shadow. */
  inheritSiteAi: (id: string) => request<AiPolicy>(`/api/setup/sites/${enc(id)}/ai`, { method: 'DELETE' }),
  /** Admin override: the level in shadow goes live now (reason 20–500 characters; audited, supervisors notified). */
  goLiveNow: (siteId: string | null, level: number, reason: string) =>
    request<AiPolicy>(siteId ? `/api/setup/sites/${enc(siteId)}/ai/go-live` : '/api/setup/ai/go-live', { method: 'POST', json: { level, reason } }),
  qa: (status: 'pending' | 'reviewed', signal?: AbortSignal) => request<QaItem[]>(`/api/qa${qs({ status })}`, { signal }),
  submitQa: (eventId: string, body: { humanClass: Verdict; notes?: string }) =>
    request<QaResult>(`/api/qa/${enc(eventId)}`, { method: 'POST', json: body }),
  agentReport: (p: { days: number; siteId?: string }, signal?: AbortSignal) =>
    request<AgentReport>(`/api/agent/report${qs({ days: p.days, siteId: p.siteId })}`, { signal }),
  /** Blind QA evidence (audited as event.qa_evidence_viewed). */
  qaEvidence: (eventId: string, signal?: AbortSignal) => request<QaEvidence>(`/api/qa/${enc(eventId)}/evidence`, { signal }),

  // Phase 4: the agent as co-pilot (api-phase4.md)
  watches: (eventId: string, signal?: AbortSignal) => request<Watch[]>(`/api/events/${enc(eventId)}/watches`, { signal }),
  /** Owner or supervisor; the event is active or parked. */
  addWatch: (eventId: string, body: WatchInput) => request<Watch>(`/api/events/${enc(eventId)}/watches`, { method: 'POST', json: body }),
  cancelWatch: (id: string) => request<{ ok: boolean }>(`/api/watches/${enc(id)}`, { method: 'DELETE' }),
  /** 409 when the site is in shadow (Level 0), the agent is paused, the event is closed, or the agent isn't running. */
  instruct: (eventId: string, text: string) => request<Instruction>(`/api/events/${enc(eventId)}/agent/instructions`, { method: 'POST', json: { text } }),
  instructions: (eventId: string, signal?: AbortSignal) => request<Instruction[]>(`/api/events/${enc(eventId)}/agent/instructions`, { signal }),
  /** Pending approvals on my events (supervisors: all). */
  approvalsMine: (signal?: AbortSignal) => request<Approval[]>('/api/approvals/mine', { signal }),
  /** 409 when it was already decided or has expired. */
  decideApproval: (id: string, decision: 'approve' | 'deny', note?: string) =>
    request<Approval>(`/api/approvals/${enc(id)}`, { method: 'POST', json: note ? { decision, note } : { decision } }),
  drafts: (eventId: string, signal?: AbortSignal) => request<AgentDrafts>(`/api/events/${enc(eventId)}/drafts`, { signal }),
  guardTours: (siteId: string, signal?: AbortSignal) => request<GuardTour[]>(`/api/setup/sites/${enc(siteId)}/guard-tours`, { signal }),
  createGuardTour: (siteId: string, body: GuardTourBody) => request<GuardTour>(`/api/setup/sites/${enc(siteId)}/guard-tours`, { method: 'POST', json: body }),
  patchGuardTour: (siteId: string, tourId: string, body: Partial<GuardTourBody>) =>
    request<GuardTour>(`/api/setup/sites/${enc(siteId)}/guard-tours/${enc(tourId)}`, { method: 'PATCH', json: body }),
  /** Switches it off; the runs stay. */
  disableGuardTour: (siteId: string, tourId: string) => request<GuardTour>(`/api/setup/sites/${enc(siteId)}/guard-tours/${enc(tourId)}`, { method: 'DELETE' }),
  guardTourRuns: (siteId: string, tourId: string, signal?: AbortSignal) =>
    request<GuardTourRun[]>(`/api/setup/sites/${enc(siteId)}/guard-tours/${enc(tourId)}/runs`, { signal }),
  /** Devstack only (admin): what the simulated detector "sees" on a camera. An empty list clears it. */
  simDetections: (cameraId: string, detections: { label: SimLabel; confidence: number }[], seconds?: number) =>
    request<{ cameraId: string; detections: { label: string; confidence: number }[] }>(`/api/dev/sim/detections/${enc(cameraId)}`, {
      method: 'PUT',
      json: seconds ? { detections, seconds } : { detections },
    }),

  // Phase 5: deter and resolve (api-phase5.md). Device commands are owner-or-supervisor and carry no version.
  devices: (eventId: string, signal?: AbortSignal) => request<EventDevices>(`/api/events/${enc(eventId)}/devices`, { signal }),
  talkdown: (eventId: string, body: { deviceIds: string[]; messageId?: string; text?: string; language?: TalkdownLanguage }) =>
    request<TalkdownResult>(`/api/events/${enc(eventId)}/talkdown`, { method: 'POST', json: body }),
  /** Push-to-talk: the recorded clip, raw, with the recorder's content type (≤ 4 MB). */
  talkdownLive: (eventId: string, deviceIds: string[], clip: Blob, contentType: string, durationMs?: number) =>
    request<TalkdownResult>(`/api/events/${enc(eventId)}/talkdown/live${qs({ deviceIds: deviceIds.join(','), durationMs: durationMs ? Math.round(durationMs) : null })}`, {
      method: 'POST',
      blob: clip,
      headers: { 'content-type': contentType },
    }),
  /** Live talk to a camera's speaker (owner-or-supervisor, one talker per camera): a one-use WebSocket path. */
  talkStart: (eventId: string, cameraId: string) =>
    request<{ actionId: string; url: string; sampleRate: number; maxSeconds: number }>(`/api/events/${enc(eventId)}/talk`, { method: 'POST', json: { cameraId } }),
  /** Listening to a camera's microphone (audited): a short-lived URL for an <audio>. */
  audioListen: (cameraId: string, eventId?: string | null) => request<{ url: string; expiresAt: string }>('/api/audio/listen', { method: 'POST', json: { cameraId, eventId: eventId ?? null } }),
  /** Speaker and microphone as NX reports them; null where NX doesn't say (offered, and NX decides). */
  audioCaps: (cameraId: string, signal?: AbortSignal) => request<{ speaker: boolean | null; microphone: boolean | null }>(`/api/audio/caps${qs({ cameraId })}`, { signal }),
  relay: (eventId: string, deviceId: string, body: { action: 'pulse' | 'hold' | 'release'; seconds?: number }) =>
    request<RelayResult>(`/api/events/${enc(eventId)}/relays/${enc(deviceId)}`, { method: 'POST', json: body }),
  /** The audio that was played (audited as media access). */
  talkdownAudioUrl: (actionId: string) => `/api/talkdown/${enc(actionId)}/audio`,
  /** Preview speech for a text (object URL). */
  talkdownPreview: (text: string, language: string) => postForBlobUrl('/api/talkdown/preview', { text, language }),
  talkdownMessages: (siteId: string | null, signal?: AbortSignal) => request<TalkdownMessage[]>(`/api/setup/talkdown-messages${qs({ siteId })}`, { signal }),
  createTalkdownMessage: (body: TalkdownMessageBody) => request<TalkdownMessage>('/api/setup/talkdown-messages', { method: 'POST', json: body }),
  patchTalkdownMessage: (id: string, body: Partial<TalkdownMessageBody> & { active?: boolean }) =>
    request<TalkdownMessage>(`/api/setup/talkdown-messages/${enc(id)}`, { method: 'PATCH', json: body }),
  notifyCustomer: (eventId: string, body: { templateCode: string; recipientIds?: string[]; summary?: string }) =>
    request<{ notifications: { id: string; status: string; to: string; error?: string }[] }>(`/api/events/${enc(eventId)}/notify`, { method: 'POST', json: body }),
  eventNotifications: (eventId: string, signal?: AbortSignal) => request<EventNotification[]>(`/api/events/${enc(eventId)}/notifications`, { signal }),
  notificationRecipients: (siteId: string, signal?: AbortSignal) => request<NotificationRecipient[]>(`/api/setup/sites/${enc(siteId)}/notification-recipients`, { signal }),
  addNotificationRecipient: (siteId: string, body: NotificationRecipientBody) =>
    request<NotificationRecipient>(`/api/setup/sites/${enc(siteId)}/notification-recipients`, { method: 'POST', json: body }),
  removeNotificationRecipient: (siteId: string, rid: string) =>
    request<{ ok: boolean }>(`/api/setup/sites/${enc(siteId)}/notification-recipients/${enc(rid)}`, { method: 'DELETE' }),
  notificationTemplates: (signal?: AbortSignal) => request<NotificationTemplate[]>('/api/setup/notification-templates', { signal }),
  notificationSettings: (siteId: string, signal?: AbortSignal) => request<NotificationSettings>(`/api/setup/sites/${enc(siteId)}/notification-settings`, { signal }),
  putNotificationSettings: (siteId: string, body: { mode?: NotifyMode; moments?: Partial<Record<NotifyMoment, string | null>> }) =>
    request<NotificationSettings>(`/api/setup/sites/${enc(siteId)}/notification-settings`, { method: 'PUT', json: body }),
  putNotificationTemplate: (code: string, channel: NotifyChannel, body: NotificationTemplateBody) =>
    request<{ ok: boolean }>(`/api/setup/notification-templates/${enc(code)}/${enc(channel)}`, { method: 'PUT', json: body }),

  // setup (admin edits; supervisors read)
  setupDispositions: (signal?: AbortSignal) => request<DispositionCode[]>('/api/setup/dispositions', { signal }),
  createDisposition: (body: DispositionCode) => request<DispositionCode>('/api/setup/dispositions', { method: 'POST', json: body }),
  patchDisposition: (code: string, body: Partial<DispositionCode>) =>
    request<DispositionCode>(`/api/setup/dispositions/${enc(code)}`, { method: 'PATCH', json: body }),
  missionTemplates: (signal?: AbortSignal) => request<MissionTemplate[]>('/api/setup/mission-templates', { signal }),
  createMissionTemplate: (body: MissionTemplateBody) =>
    request<MissionTemplate>('/api/setup/mission-templates', { method: 'POST', json: body }),
  updateMissionTemplate: (id: string, body: MissionTemplateBody) =>
    request<MissionTemplate>(`/api/setup/mission-templates/${enc(id)}`, { method: 'PUT', json: body }),
  siteSetup: (id: string, signal?: AbortSignal) => request<SiteSetup>(`/api/setup/sites/${enc(id)}`, { signal }),
  patchSiteOps: (id: string, body: Partial<SiteOps>) =>
    request<SiteSetup>(`/api/setup/sites/${enc(id)}/ops`, { method: 'PATCH', json: body }),
  putSiteMissions: (id: string, missions: { eventType: string; templateId: string }[]) =>
    request<SiteSetup>(`/api/setup/sites/${enc(id)}/missions`, { method: 'PUT', json: { missions } }),
  putCallProcedure: (id: string, steps: CallProcedureStepBody[]) =>
    request<SiteSetup>(`/api/setup/sites/${enc(id)}/call-procedure`, { method: 'PUT', json: { steps } }),
  goLive: (id: string) => request<SiteSetup>(`/api/setup/sites/${enc(id)}/go-live`, { method: 'POST', json: {} }),
  undoGoLive: (id: string, reason: string) =>
    request<SiteSetup>(`/api/setup/sites/${enc(id)}/go-live/undo`, { method: 'POST', json: { reason } }),
  authorities: (signal?: AbortSignal) => request<Authority[]>('/api/setup/authorities', { signal }),
  createAuthority: (body: Omit<Authority, 'id'>) => request<Authority>('/api/setup/authorities', { method: 'POST', json: body }),
  patchAuthority: (id: string, body: Partial<Omit<Authority, 'id'>>) =>
    request<Authority>(`/api/setup/authorities/${enc(id)}`, { method: 'PATCH', json: body }),
  setupUsers: (signal?: AbortSignal) => request<SetupUser[]>('/api/setup/users', { signal }),
  patchSetupUser: (id: string, body: SetupUserPatch) => request<SetupUser>(`/api/setup/users/${enc(id)}`, { method: 'PATCH', json: body }),

  // exports and audit
  closedEventsExportUrl: (p: { from: string; to: string; format: 'csv' | 'json' }) => `/api/exports/closed-events${qs(p)}`,
  audit: (p: AuditQuery, signal?: AbortSignal) =>
    request<AuditRow[]>(
      `/api/audit${qs({ subjectType: p.subjectType, subjectId: p.subjectId, siteId: p.siteId, actorId: p.actorId, action: p.action, from: p.from, to: p.to, limit: p.limit })}`,
      { signal },
    ),
  auditVerify: (day: string, signal?: AbortSignal) => request<AuditVerify>(`/api/audit/verify${qs({ day })}`, { signal }),
  setWorkItemStatus: (id: string, status: 'open' | 'passed_to_erp_owner' | 'resolved') =>
    request<WorkItem>(`/api/sync/work-items/${enc(id)}/status`, { method: 'POST', json: { status } }),

  // imports
  uploadImport: (fileName: string, text: string) =>
    request<ImportRecord>('/api/sync/imports', {
      method: 'POST',
      rawBody: text,
      headers: { 'x-file-name': fileName, 'content-type': 'application/json' },
    }),
  imports: (signal?: AbortSignal) => request<ImportRecord[]>('/api/sync/imports', { signal }),
  importRecord: (id: string, signal?: AbortSignal) => request<ImportRecord>(`/api/sync/imports/${enc(id)}`, { signal }),
  confirmImport: (id: string) => request<ImportRecord>(`/api/sync/imports/${enc(id)}/confirm`, { method: 'POST' }),
  cancelImport: (id: string) => request<ImportRecord>(`/api/sync/imports/${enc(id)}/cancel`, { method: 'POST' }),
  holds: (status: string | undefined = 'held', signal?: AbortSignal) =>
    request<Hold[]>(`/api/sync/holds${qs({ status })}`, { signal }),
  confirmHold: (id: string, note?: string) =>
    request<Hold>(`/api/sync/holds/${enc(id)}/confirm`, { method: 'POST', json: note ? { note } : {} }),
  rejectHold: (id: string, note?: string) =>
    request<Hold>(`/api/sync/holds/${enc(id)}/reject`, { method: 'POST', json: note ? { note } : {} }),
  deadLetters: (signal?: AbortSignal) => request<DeadLetter[]>('/api/sync/dead-letters', { signal }),
  retryDeadLetter: (id: string) =>
    request<{ result: Json }>(`/api/sync/dead-letters/${enc(id)}/retry`, { method: 'POST' }),
  discardDeadLetter: (id: string) =>
    request<DeadLetter>(`/api/sync/dead-letters/${enc(id)}/discard`, { method: 'POST' }),
  workItems: (status: string | undefined = 'open', signal?: AbortSignal) =>
    request<WorkItem[]>(`/api/sync/work-items${qs({ status })}`, { signal }),
  applyLog: (limit = 200, signal?: AbortSignal) =>
    request<ApplyLogEntry[]>(`/api/sync/apply-log${qs({ limit })}`, { signal }),

  // Phase 6: ERP Sync monitor and shadow cutover
  erpStatus: (signal?: AbortSignal) => request<ErpStatus>('/api/sync/erp/status', { signal }),
  erpInbox: (p: { status?: string; limit?: number } = {}, signal?: AbortSignal) =>
    request<InboxMessage[]>(`/api/sync/erp/inbox${qs({ status: p.status, limit: p.limit })}`, { signal }),
  erpInboxMessage: (id: string, signal?: AbortSignal) => request<InboxMessageDetail>(`/api/sync/erp/inbox/${enc(id)}`, { signal }),
  erpRetryFailed: () => request<{ requeued: number }>('/api/sync/erp/inbox/retry-failed', { method: 'POST', json: {} }),
  erpDeliveries: (p: { status?: string; limit?: number } = {}, signal?: AbortSignal) =>
    request<Delivery[]>(`/api/sync/erp/deliveries${qs({ status: p.status, limit: p.limit })}`, { signal }),
  erpReplayDelivery: (id: string) => request<{ ok: true }>(`/api/sync/erp/deliveries/${enc(id)}/replay`, { method: 'POST', json: {} }),
  erpDiscardDelivery: (id: string) => request<{ ok: true }>(`/api/sync/erp/deliveries/${enc(id)}/discard`, { method: 'POST', json: {} }),
  erpResumeDelivery: () => request<{ ok: true }>('/api/sync/erp/delivery/resume', { method: 'POST', json: {} }),
  erpResyncRequests: (signal?: AbortSignal) => request<ResyncRequest[]>('/api/sync/erp/resync-requests', { signal }),
  erpResync: (scope: { kind: 'site'; site_id: string } | { kind: 'all' }, note?: string) =>
    request<{ requestId: string }>('/api/sync/erp/resync', { method: 'POST', json: note ? { scope, note } : { scope } }),
  erpDigests: (signal?: AbortSignal) => request<Digest[]>('/api/sync/erp/digests', { signal }),
  erpDigest: (id: string, signal?: AbortSignal) => request<DigestDetail>(`/api/sync/erp/digests/${enc(id)}`, { signal }),
  erpParked: (signal?: AbortSignal) => request<ParkedMessage[]>('/api/sync/erp/parked', { signal }),
  cutover: (signal?: AbortSignal) => request<CutoverState>('/api/sync/cutover', { signal }),
  cutoverShadowOn: (initialLoad: boolean) => request<{ requestId: string | null }>('/api/sync/cutover/shadow', { method: 'POST', json: { initialLoad } }),
  cutoverShadowOff: () => request<{ ok: true }>('/api/sync/cutover/shadow-off', { method: 'POST', json: {} }),
  cutoverDiff: (signal?: AbortSignal) => request<CutoverDiff>('/api/sync/cutover/diff', { signal }),
  cutoverAccept: (entityType: string, entityId: string, note: string) =>
    request<{ ok: true }>('/api/sync/cutover/accept', { method: 'POST', json: { entityType, entityId, note } }),
  cutoverUnaccept: (entityType: string, entityId: string) =>
    request<{ ok: true }>(`/api/sync/cutover/accept/${enc(entityType)}/${enc(entityId)}`, { method: 'DELETE' }),
  cutoverPromote: (deliverBacklog: boolean) => request<PromoteResult>('/api/sync/cutover/promote', { method: 'POST', json: { deliverBacklog } }),
  cutoverRollback: () => request<RollbackResult>('/api/sync/cutover/rollback', { method: 'POST', json: {} }),

  // alarm ingest admin
  ingestAddresses: (signal?: AbortSignal) => request<IngestAddress[]>('/api/ingest/addresses', { signal }),
  createIngestAddress: (body: { siteId: string; parserTemplateId?: string; localPart?: string }) =>
    request<IngestAddress>('/api/ingest/addresses', { method: 'POST', json: body }),
  patchIngestAddress: (id: string, body: { active?: boolean; parserTemplateId?: string | null }) =>
    request<IngestAddress>(`/api/ingest/addresses/${enc(id)}`, { method: 'PATCH', json: body }),
  templates: (signal?: AbortSignal) => request<ParserTemplate[]>('/api/ingest/templates', { signal }),
  activateTemplate: (id: string) =>
    request<ParserTemplate>(`/api/ingest/templates/${enc(id)}/activate`, { method: 'POST' }),
  messages: (p: { status?: string; limit?: number }, signal?: AbortSignal) =>
    request<InboundMessage[]>(`/api/ingest/messages${qs({ status: p.status, limit: p.limit ?? 100 })}`, { signal }),
  rawMessageUrl: (id: string) => `/api/ingest/messages/${enc(id)}/raw`,
  testParse: (body: { inboundMessageId: string; templateId: string }) =>
    request<TestParseResult>('/api/ingest/test-parse', { method: 'POST', json: body }),
};

/** Builds the EventSource URL for /api/stream with the auth query params the server accepts. */
export async function streamUrl(): Promise<string> {
  const params = auth ? await auth.streamParams() : {};
  return `/api/stream${qs(params)}`;
}
